import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import InstituteLogin from './InstituteLogin';
import { getApiBaseUrl } from './config/apiConfig';
import { 
  getInstituteForUser, 
  getInstituteSessions, 
  createInstituteSession, 
  updateInstituteSession, 
  deleteInstituteSession,
  getInstituteRequests,
  updateInstituteRequestStatus,
  getSessionTemplates,
  createSessionTemplate,
  updateSessionTemplate,
  deleteSessionTemplate,
  getWaitlistForInstitute,
  notifyWaitlistStudent,
  removeFromWaitlist,
  getGlobalStudentSchoolStats
} from './instituteService';
import { supabase } from './supabaseClient';
import { FaSchool, FaPlus, FaEdit, FaTrash, FaCheck, FaTimes, FaClock, FaCalendarAlt, FaUser, FaMapMarkerAlt, FaUsers, FaEnvelope, FaPhone, FaChartLine, FaBell, FaGraduationCap, FaDollarSign, FaExclamationCircle, FaUpload, FaImage, FaSignOutAlt, FaCog, FaLock, FaSave, FaCheckCircle, FaCopy, FaSearch, FaFilter, FaFileAlt, FaListUl, FaChartPie, FaChevronDown } from 'react-icons/fa';

export default function InstituteDashboard() {
  const navigate = useNavigate();
  const [isArabic, setIsArabic] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'sessions', 'courses', 'requests', 'students', 'settings', 'templates', 'waitlist', 'calendar'
  const [institute, setInstitute] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [requests, setRequests] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [schoolStats, setSchoolStats] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'session', 'course'
  const [filterFormat, setFilterFormat] = useState('all'); // 'all', 'online', 'in_person'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'upcoming', 'past', 'full'
  const [selectedDate, setSelectedDate] = useState(null); // For calendar view
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    title: '',
    teacher_name: '',
    teacher_email: '',
    teacher_image: '',
    subject: '',
    session_type: 'session',
    format: 'in_person',
    place: '',
    time_from: '',
    time_to: '',
    max_students: 10,
    description: '',
    price: ''
  });
  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [sessionForm, setSessionForm] = useState({
    title: '',
    teacher_name: '',
    teacher_email: '',
    teacher_image: '',
    subject: '',
    session_type: 'session', // 'session' or 'course'
    format: 'in_person', // 'in_person' or 'online'
    place: '',
    meeting_link: '',
    date: '', // For sessions (single date)
    start_date: '', // For courses (start date)
    end_date: '', // For courses (end date)
    time_from: '',
    time_to: '',
    max_students: 10,
    description: '',
    price: ''
  });
  const [teacherImagePreview, setTeacherImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [stats, setStats] = useState({
    totalSessions: 0,
    activeSessions: 0,
    completedSessions: 0,
    upcomingSessions: 0,
    totalStudents: 0,
    pendingRequests: 0,
    acceptedRequests: 0,
    totalRevenue: 0
  });
  const [students, setStudents] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    description: '',
    logo_url: ''
  });
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [settingsSuccess, setSettingsSuccess] = useState('');
  const [settingsError, setSettingsError] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const isMobile = window.innerWidth <= 600;

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [userEmail, setUserEmail] = useState('');

  const requestStatusStats = useMemo(() => {
    if (!requests || requests.length === 0) return { total: 0, items: [] };

    const counts = { accepted: 0, pending: 0, declined: 0, cancelled: 0 };
    requests.forEach((r) => {
      const status = (r.status || 'pending').toLowerCase();
      if (status === 'accepted') counts.accepted += 1;
      else if (status === 'pending') counts.pending += 1;
      else if (status === 'declined' || status === 'rejected') counts.declined += 1;
      else if (status === 'cancelled' || status === 'canceled') counts.cancelled += 1;
    });

    const total = Object.values(counts).reduce((sum, v) => sum + v, 0);
    const items = [
      { key: 'accepted', labelEn: 'Accepted', labelAr: 'مقبولة', color: '#22c55e', count: counts.accepted },
      { key: 'pending', labelEn: 'Pending', labelAr: 'قيد الانتظار', color: '#fbbf24', count: counts.pending },
      { key: 'declined', labelEn: 'Declined', labelAr: 'مرفوضة', color: '#ef4444', count: counts.declined },
      { key: 'cancelled', labelEn: 'Cancelled', labelAr: 'ملغاة', color: '#9ca3af', count: counts.cancelled }
    ].map((item) => ({
      ...item,
      percent: total ? Math.round((item.count / total) * 100) : 0
    }));

    return { total, items };
  }, [requests]);

  // Analytics computations
  const analyticsData = useMemo(() => {
    const today = new Date();
    const last30Days = new Date(today);
    last30Days.setDate(last30Days.getDate() - 30);
    const last7Days = new Date(today);
    last7Days.setDate(last7Days.getDate() - 7);

    // Requests over time (last 30 days)
    const requestsOverTime = [];
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const count = requests.filter(r => {
        const reqDate = new Date(r.created_at).toISOString().split('T')[0];
        return reqDate === dateStr;
      }).length;
      requestsOverTime.push({ date: dateStr, count });
    }

    // Sessions over time (last 30 days)
    const sessionsOverTime = [];
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const count = sessions.filter(s => {
        const sessionDate = new Date(s.date).toISOString().split('T')[0];
        return sessionDate === dateStr;
      }).length;
      sessionsOverTime.push({ date: dateStr, count });
    }

    // Session type distribution
    const sessionTypeCounts = { session: 0, course: 0 };
    sessions.forEach(s => {
      const type = (s.session_type || 'session').toLowerCase();
      if (type === 'course') sessionTypeCounts.course += 1;
      else sessionTypeCounts.session += 1;
    });
    const sessionTypeTotal = sessionTypeCounts.session + sessionTypeCounts.course;
    const sessionTypeStats = [
      { key: 'session', labelEn: 'Sessions', labelAr: 'جلسات', count: sessionTypeCounts.session, color: '#3b82f6' },
      { key: 'course', labelEn: 'Courses', labelAr: 'دورات', count: sessionTypeCounts.course, color: '#8b5cf6' }
    ].map(item => ({
      ...item,
      percent: sessionTypeTotal ? Math.round((item.count / sessionTypeTotal) * 100) : 0
    }));

    // Format distribution
    const formatCounts = { in_person: 0, online: 0 };
    sessions.forEach(s => {
      const format = (s.format || 'in_person').toLowerCase();
      if (format === 'online') formatCounts.online += 1;
      else formatCounts.in_person += 1;
    });
    const formatTotal = formatCounts.in_person + formatCounts.online;
    const formatStats = [
      { key: 'in_person', labelEn: 'In-Person', labelAr: 'حضوري', count: formatCounts.in_person, color: '#10b981' },
      { key: 'online', labelEn: 'Online', labelAr: 'أونلاين', count: formatCounts.online, color: '#6366f1' }
    ].map(item => ({
      ...item,
      percent: formatTotal ? Math.round((item.count / formatTotal) * 100) : 0
    }));

    // Revenue over time (last 30 days)
    const revenueOverTime = [];
    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const revenue = sessions
        .filter(s => {
          const sessionDate = new Date(s.date).toISOString().split('T')[0];
          return sessionDate === dateStr;
        })
        .reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0);
      revenueOverTime.push({ date: dateStr, revenue });
    }

    // Recent activity (last 7 days)
    const recentRequests = requests.filter(r => new Date(r.created_at) >= last7Days).length;
    const recentSessions = sessions.filter(s => new Date(s.date) >= last7Days).length;
    const recentRevenue = sessions
      .filter(s => new Date(s.date) >= last7Days)
      .reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0);

    // Acceptance rate
    const totalRequests = requests.length;
    const acceptedRequests = requests.filter(r => r.status === 'accepted').length;
    const acceptanceRate = totalRequests > 0 ? Math.round((acceptedRequests / totalRequests) * 100) : 0;

    return {
      requestsOverTime,
      sessionsOverTime,
      sessionTypeStats,
      formatStats,
      revenueOverTime,
      recentRequests,
      recentSessions,
      recentRevenue,
      acceptanceRate
    };
  }, [requests, sessions]);

  // Check if user is institute admin
  useEffect(() => {
    async function checkAccess() {
      setCheckingAuth(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsAuthenticated(false);
        setCheckingAuth(false);
        return;
      }

      // Store and display user email
      const storedEmail = localStorage.getItem('instituteEmail') || user.email || '';
      setUserEmail(storedEmail);
      // Always keep email stored for current session
      localStorage.setItem('instituteEmail', user.email || '');

      const { data, error } = await getInstituteForUser(user.email);
      
      if (error || !data || !data.institutes) {
        setIsAuthenticated(false);
        setCheckingAuth(false);
        return;
      }

      // User is authenticated and has institute access - show dashboard
      setIsAuthenticated(true);
      setInstitute(data.institutes);
      // Initialize settings form with institute data
      setSettingsForm({
        name: data.institutes.name || '',
        email: data.institutes.email || '',
        phone: data.institutes.phone || '',
        location: data.institutes.location || '',
        description: data.institutes.description || '',
        logo_url: data.institutes.logo_url || ''
      });
      setLogoPreview(data.institutes.logo_url || null);
      await loadData(data.institutes.id);
      await loadStats(data.institutes.id);
      await loadStudents(data.institutes.id);
      await loadNotifications(data.institutes.id);
      await loadSchoolStats();
      setCheckingAuth(false);
    }
    checkAccess();
    
    // Listen for auth state changes (in case user signs in on this page)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        checkAccess();
      } else if (event === 'SIGNED_OUT') {
        setIsAuthenticated(false);
        setInstitute(null);
      }
    });
    
    return () => {
      subscription?.unsubscribe();
    };
  }, [navigate, isArabic]);

  const loadData = async (instituteId) => {
    try {
      setLoading(true);
      const [sessionsData, requestsData, templatesData, waitlistData] = await Promise.all([
        getInstituteSessions(instituteId),
        getInstituteRequests(instituteId),
        getSessionTemplates(instituteId),
        getWaitlistForInstitute(instituteId)
      ]);

      if (sessionsData.data) setSessions(sessionsData.data);
      if (requestsData.data) setRequests(requestsData.data);
      if (templatesData.data) setTemplates(templatesData.data);
      if (waitlistData.data) setWaitlist(waitlistData.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async (instituteId) => {
    try {
      const [sessionsData, requestsData] = await Promise.all([
        getInstituteSessions(instituteId),
        getInstituteRequests(instituteId)
      ]);

      const allSessions = sessionsData.data || [];
      const allRequests = requestsData.data || [];
      const today = new Date().toISOString().split('T')[0];

      const activeSessions = allSessions.filter(s => s.is_active).length;
      const upcomingSessions = allSessions.filter(s => new Date(s.date) >= new Date(today)).length;
      const completedSessions = allSessions.filter(s => new Date(s.date) < new Date(today)).length;
      
      const uniqueStudents = new Set(allRequests.map(r => r.student_email)).size;
      const pendingRequests = allRequests.filter(r => r.status === 'pending').length;
      const acceptedRequests = allRequests.filter(r => r.status === 'accepted').length;
      
      const totalRevenue = allSessions.reduce((sum, s) => sum + (parseFloat(s.price) || 0), 0);

      setStats({
        totalSessions: allSessions.length,
        activeSessions,
        completedSessions,
        upcomingSessions,
        totalStudents: uniqueStudents,
        pendingRequests,
        acceptedRequests,
        totalRevenue
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadSchoolStats = async () => {
    try {
      const result = await getGlobalStudentSchoolStats();
      if (result && result.success !== false) {
        setSchoolStats({
          total: result.total || 0,
          items: result.items || []
        });
      }
    } catch (error) {
      console.error('Error loading global student school analytics:', error);
    }
  };

  const loadStudents = async (instituteId) => {
    try {
      const { data: requestsData } = await getInstituteRequests(instituteId);
      if (requestsData) {
        // Get unique students with their request counts
        const studentMap = new Map();
        requestsData.forEach(request => {
          if (!studentMap.has(request.student_email)) {
            studentMap.set(request.student_email, {
              email: request.student_email,
              name: request.student_name,
              phone: request.student_phone,
              totalRequests: 0,
              acceptedRequests: 0,
              pendingRequests: 0,
              lastRequestDate: request.created_at,
              school: null
            });
          }
          const student = studentMap.get(request.student_email);
          student.totalRequests++;
          if (request.status === 'accepted') student.acceptedRequests++;
          if (request.status === 'pending') student.pendingRequests++;
          if (new Date(request.created_at) > new Date(student.lastRequestDate)) {
            student.lastRequestDate = request.created_at;
          }
        });

        const studentList = Array.from(studentMap.values());

        // Enrich with school/university from students table for analytics
        const emails = studentList.map(s => s.email).filter(Boolean);
        if (emails.length > 0) {
          try {
            const { data: profileRows } = await supabase
              .from('students')
              .select('email, school')
              .in('email', emails);

            const schoolByEmail = {};
            (profileRows || []).forEach(row => {
              if (row.email) {
                schoolByEmail[row.email] = row.school || null;
              }
            });

            studentList.forEach(s => {
              if (schoolByEmail[s.email]) {
                s.school = schoolByEmail[s.email];
              }
            });
          } catch (schoolError) {
            console.error('Error loading student schools for analytics:', schoolError);
          }
        }

        setStudents(studentList);
      }
    } catch (error) {
      console.error('Error loading students:', error);
    }
  };

  const loadNotifications = async (instituteId) => {
    try {
      const { data: requestsData } = await getInstituteRequests(instituteId);
      const { data: sessionsData } = await getInstituteSessions(instituteId);
      
      const notifs = [];
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];

      // New pending requests
      const newRequests = (requestsData || []).filter(r => r.status === 'pending');
      newRequests.forEach(req => {
        notifs.push({
          id: `req-${req.id}`,
          type: 'new_request',
          title: 'New Session Request',
          message: `${req.student_name} requested a session: ${req.title}`,
          time: req.created_at,
          link: '#requests',
          unread: true
        });
      });

      // Upcoming sessions (next 24 hours)
      const upcomingSessions = (sessionsData || []).filter(s => {
        const sessionDate = new Date(s.date);
        return sessionDate >= today && sessionDate <= tomorrow;
      });
      upcomingSessions.forEach(session => {
        notifs.push({
          id: `session-${session.id}`,
          type: 'upcoming_session',
          title: 'Upcoming Session',
          message: `Session "${session.title}" is scheduled for tomorrow`,
          time: session.date,
          link: '#sessions',
          unread: true
        });
      });

      // Sort by time (newest first)
      notifs.sort((a, b) => new Date(b.time) - new Date(a.time));
      setNotifications(notifs.slice(0, 10)); // Keep last 10
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const handleOpenSessionModal = (session = null) => {
    // Determine session_type based on active tab
    let lockedSessionType = null;
    if (activeTab === 'courses') {
      lockedSessionType = 'course';
    } else if (activeTab === 'sessions') {
      lockedSessionType = 'session';
    }

    if (session && session.id) {
      // Editing existing session/course - use locked type from tab if available, otherwise keep original
      const sessionType = lockedSessionType || session.session_type || 'session';
      setEditingSession(session);
      setSessionForm({
        title: session.title,
        teacher_name: session.teacher_name,
        teacher_email: session.teacher_email || '',
        teacher_image: session.teacher_image || '',
        subject: session.subject || '',
        session_type: sessionType,
        format: session.format || 'in_person',
        place: session.place || '',
        meeting_link: session.meeting_link || '',
        date: session.date || '',
        start_date: session.start_date || session.date || '',
        end_date: session.end_date || session.date || '',
        time_from: session.time_from,
        time_to: session.time_to,
        max_students: session.max_students,
        description: session.description || '',
        price: session.price || ''
      });
      setTeacherImagePreview(session.teacher_image || null);
    } else {
      // Creating new session/course - use type from tab or passed value
      const sessionType = session?.session_type || lockedSessionType || 'session';
      setEditingSession(null);
      setSessionForm({
        title: '',
        teacher_name: '',
        teacher_email: '',
        teacher_image: '',
        subject: '',
        session_type: sessionType,
        format: 'in_person',
        place: '',
        meeting_link: '',
        date: '',
        start_date: '',
        end_date: '',
        time_from: '',
        time_to: '',
        max_students: 10,
        description: '',
        price: ''
      });
      setTeacherImagePreview(null);
    }
    setSessionModalOpen(true);
  };

  const handleTeacherImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert(isArabic ? 'يرجى اختيار ملف صورة' : 'Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(isArabic ? 'حجم الصورة كبير جداً (الحد الأقصى 5MB)' : 'Image size too large (max 5MB)');
      return;
    }

    if (!institute || !institute.id) {
      alert(isArabic ? 'يرجى الانتظار حتى يتم تحميل بيانات المعهد' : 'Please wait for institute data to load');
      return;
    }

    if (!institute || !institute.id) {
      alert(isArabic ? 'يرجى الانتظار حتى يتم تحميل بيانات المعهد' : 'Please wait for institute data to load');
      return;
    }

    setUploadingImage(true);
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }

      const fileExt = file.name.split('.').pop();
      const fileName = `teacher-${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `institute-teachers/${institute.id}/${fileName}`;

      // Use profile-pictures bucket (already exists and configured)
      const bucketName = 'profile-pictures';
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        console.error('Upload error details:', uploadError);
        // Show more detailed error message
        const errorMessage = uploadError.message || JSON.stringify(uploadError);
        if (errorMessage.includes('Bucket not found') || errorMessage.includes('does not exist')) {
          alert(isArabic 
            ? `خطأ: الحاوية "${bucketName}" غير موجودة. يرجى إنشاء حاوية التخزين في Supabase Storage.`
            : `Error: Bucket "${bucketName}" not found. Please create the storage bucket in Supabase Storage.`
          );
        } else if (errorMessage.includes('row-level security') || errorMessage.includes('RLS')) {
          alert(isArabic 
            ? 'خطأ: لا توجد صلاحيات للرفع. يرجى التحقق من إعدادات الأمان (RLS) في Supabase Storage.'
            : 'Error: No permission to upload. Please check Supabase Storage security settings (RLS policies).'
          );
        } else {
          alert(isArabic 
            ? `فشل رفع الصورة: ${errorMessage}`
            : `Failed to upload image: ${errorMessage}`
          );
        }
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(bucketName)
        .getPublicUrl(filePath);

      if (!publicUrl) {
        throw new Error('Failed to get public URL');
      }

      setSessionForm(prev => ({ ...prev, teacher_image: publicUrl }));
      setTeacherImagePreview(publicUrl);
    } catch (error) {
      console.error('Error uploading image:', error);
      // Error messages are already shown above, only show generic if not already shown
      if (!error.message || (!error.message.includes('Bucket not found') && !error.message.includes('row-level security') && !error.message.includes('RLS'))) {
        const errorMsg = error.message || 'Unknown error occurred';
        if (!errorMsg.includes('User not authenticated') && !errorMsg.includes('Failed to get public URL')) {
          alert(isArabic 
            ? `فشل رفع الصورة: ${errorMsg}`
            : `Failed to upload image: ${errorMsg}`
          );
        }
      }
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveTeacherImage = () => {
    setSessionForm(prev => ({ ...prev, teacher_image: '' }));
    setTeacherImagePreview(null);
  };

  const handleLogout = async () => {
    if (window.confirm(isArabic ? 'هل أنت متأكد من تسجيل الخروج؟' : 'Are you sure you want to logout?')) {
      await supabase.auth.signOut();
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userRole');
      localStorage.removeItem('instituteRememberedEmail');
      navigate('/institute');
    }
  };

  const handleSaveSession = async () => {
    // Validate required fields based on session type
    if (sessionForm.session_type === 'course') {
      // Course validation: need start_date and end_date
      if (!sessionForm.title || !sessionForm.teacher_name || !sessionForm.start_date || !sessionForm.end_date) {
        alert(isArabic ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
        return;
      }
      if (new Date(sessionForm.end_date) < new Date(sessionForm.start_date)) {
        alert(isArabic ? 'تاريخ النهاية يجب أن يكون بعد تاريخ البداية' : 'End date must be after start date');
        return;
      }
    } else {
      // Session validation: need single date
      if (!sessionForm.title || !sessionForm.teacher_name || !sessionForm.date) {
        alert(isArabic ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
        return;
      }
    }

    // If in_person, place is required. If online, generate meeting link if not exists
    if (sessionForm.format === 'in_person' && !sessionForm.place) {
      alert(isArabic ? 'يرجى إدخال المكان للجلسات الحضورية' : 'Please enter location for in-person sessions');
      return;
    }

    setSubmitting(true);
    try {
      let meetingLink = sessionForm.meeting_link;
      
      // Determine date to use (for courses use start_date, for sessions use date)
      const dateForMeeting = sessionForm.session_type === 'course' ? sessionForm.start_date : sessionForm.date;

      // Generate meeting link for online sessions if not provided
      if (sessionForm.format === 'online' && !meetingLink) {
        try {
          // Generate Google Meet link (simpler than Zoom)
          const response = await fetch(`${getApiBaseUrl()}/api/create-google-meet-link`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              title: sessionForm.title,
              date: dateForMeeting,
              timeFrom: sessionForm.time_from,
              timeTo: sessionForm.time_to,
              teacherName: sessionForm.teacher_name,
              teacherEmail: sessionForm.teacher_email
            })
          });

          if (response.ok) {
            const data = await response.json();
            meetingLink = data.meetingLink || data.hangoutLink;
          } else {
            // Fallback: create a simple Google Meet link
            const timestamp = Date.now().toString(36);
            meetingLink = `https://meet.google.com/${timestamp}-${Math.random().toString(36).substring(2, 9)}`;
          }
        } catch (error) {
          console.error('Error generating meeting link:', error);
          // Fallback: create a simple Google Meet link
          const timestamp = Date.now().toString(36);
          meetingLink = `https://meet.google.com/${timestamp}-${Math.random().toString(36).substring(2, 9)}`;
        }
      }

      // Prepare session data
      const sessionData = {
        institute_id: institute.id,
        ...sessionForm,
        // For courses: use start_date as date (for backward compatibility), and include start_date/end_date
        // For sessions: use date as before
        date: sessionForm.session_type === 'course' ? sessionForm.start_date : sessionForm.date,
        meeting_link: sessionForm.format === 'online' ? meetingLink : null,
        place: sessionForm.format === 'in_person' ? sessionForm.place : null,
        price: sessionForm.price ? parseFloat(sessionForm.price) : null
      };

      if (editingSession) {
        const { error } = await updateInstituteSession(editingSession.id, sessionData);
        if (error) throw error;
      } else {
        const { error } = await createInstituteSession(sessionData);
        if (error) throw error;
      }

      await loadData(institute.id);
      await loadStats(institute.id);
      setSessionModalOpen(false);
      alert(isArabic ? 'تم الحفظ بنجاح' : 'Saved successfully');
    } catch (error) {
      console.error('Error saving session:', error);
      alert(isArabic ? 'حدث خطأ أثناء الحفظ' : 'Error saving session');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm(isArabic ? 'هل أنت متأكد من حذف هذه الجلسة؟' : 'Are you sure you want to delete this session?')) {
      return;
    }

    try {
      const { error } = await deleteInstituteSession(sessionId);
      if (error) throw error;
      await loadData(institute.id);
      await loadStats(institute.id);
      alert(isArabic ? 'تم الحذف بنجاح' : 'Deleted successfully');
    } catch (error) {
      console.error('Error deleting session:', error);
      alert(isArabic ? 'حدث خطأ أثناء الحذف' : 'Error deleting session');
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.title) {
      alert(isArabic ? 'يرجى إدخال اسم القالب والعنوان' : 'Please enter template name and title');
      return;
    }

    try {
      setSubmitting(true);
      
      // Calculate duration if time_from and time_to are provided
      let durationMinutes = null;
      if (templateForm.time_from && templateForm.time_to) {
        const [fromHours, fromMins] = templateForm.time_from.split(':').map(Number);
        const [toHours, toMins] = templateForm.time_to.split(':').map(Number);
        const fromMinutes = fromHours * 60 + fromMins;
        const toMinutes = toHours * 60 + toMins;
        durationMinutes = toMinutes - fromMinutes;
      }

      const templateData = {
        name: templateForm.name,
        title: templateForm.title,
        teacher_name: templateForm.teacher_name || null,
        teacher_email: templateForm.teacher_email || null,
        teacher_image: templateForm.teacher_image || null,
        subject: templateForm.subject || null,
        session_type: templateForm.session_type,
        format: templateForm.format,
        place: templateForm.format === 'in_person' ? templateForm.place : null,
        time_from: templateForm.time_from || null,
        time_to: templateForm.time_to || null,
        duration_minutes: durationMinutes,
        max_students: templateForm.max_students || 10,
        description: templateForm.description || null,
        price: templateForm.price ? parseFloat(templateForm.price) : null
      };

      if (editingTemplate) {
        const { error } = await updateSessionTemplate(editingTemplate.id, templateData);
        if (error) throw error;
      } else {
        const { error } = await createSessionTemplate(institute.id, templateData);
        if (error) throw error;
      }

      await loadData(institute.id);
      setTemplateModalOpen(false);
      setEditingTemplate(null);
      setTemplateForm({
        name: '',
        title: '',
        teacher_name: '',
        teacher_email: '',
        teacher_image: '',
        subject: '',
        session_type: 'session',
        format: 'in_person',
        place: '',
        time_from: '',
        time_to: '',
        max_students: 10,
        description: '',
        price: ''
      });
      alert(isArabic ? 'تم الحفظ بنجاح' : 'Template saved successfully');
    } catch (error) {
      console.error('Error saving template:', error);
      alert(isArabic ? 'حدث خطأ أثناء الحفظ' : 'Error saving template');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateRequestStatus = async (requestId, status) => {
    try {
      const { error } = await updateInstituteRequestStatus(requestId, status);
      if (error) throw error;
      await loadData(institute.id);
      await loadStats(institute.id);
      await loadStudents(institute.id);
      await loadNotifications(institute.id);
      alert(isArabic 
        ? `تم ${status === 'accepted' ? 'قبول' : 'رفض'} الطلب بنجاح`
        : `Request ${status === 'accepted' ? 'accepted' : 'declined'} successfully`
      );
    } catch (error) {
      console.error('Error updating request:', error);
      alert(isArabic ? 'حدث خطأ' : 'Error updating request');
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(isArabic ? 'ar-BH' : 'en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return '';
    if (!endDate || startDate === endDate) {
      return formatDate(startDate);
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    const startFormatted = start.toLocaleDateString(isArabic ? 'ar-BH' : 'en-US', {
      month: 'short',
      day: 'numeric'
    });
    const endFormatted = end.toLocaleDateString(isArabic ? 'ar-BH' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    return `${startFormatted} - ${endFormatted}`;
  };


  // Close notifications when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showNotifications && !event.target.closest('[data-notifications]')) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifications]);

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <InstituteLogin />;
  }

  if (checkingAuth || loading || !institute) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f7f9fb' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: 16, color: '#6b7280' }}>
            {isArabic ? 'جاري التحميل...' : 'Loading...'}
          </div>
        </div>
      </div>
    );
  }

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const acceptedRequests = requests.filter(r => r.status === 'accepted');
  const declinedRequests = requests.filter(r => r.status === 'declined');

  return (
    <div style={{
      minHeight: '100vh',
      background: '#f7f9fb',
      fontFamily: "'IBM Plex Sans Arabic', sans-serif"
    }}
    dir={isArabic ? 'rtl' : 'ltr'}
    >
      <main style={{
        padding: isMobile ? '20px 16px' : '40px 48px',
        width: '100%',
        maxWidth: '1400px',
        margin: '0 auto'
      }}
      >
        {/* Custom Header with Logo, Institute Name, Email, and Actions */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 32,
          paddingBottom: 20,
          borderBottom: '2px solid #e5e7eb'
        }}>
          {/* Left: Logo + Institute Name */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            flex: 1
          }}>
            <div
              onClick={() => setActiveTab('overview')}
              style={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              {institute.logo_url ? (
                <img
                  src={institute.logo_url}
                  alt={institute.name}
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: 12,
                    objectFit: 'cover',
                    border: '2px solid #e5e7eb',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                />
              ) : (
                <div style={{
                  width: 50,
                  height: 50,
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid #e5e7eb',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <FaSchool size={24} color="#fff" />
                </div>
              )}
              <div>
                <h1 style={{
                  fontSize: 24,
                  fontWeight: 800,
                  color: '#111827',
                  margin: 0,
                  marginBottom: 4
                }}>
                  {institute.name}
                </h1>
                {userEmail && (
                  <div style={{
                    fontSize: 13,
                    color: '#6b7280',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6
                  }}>
                    <FaEnvelope size={12} />
                    <span>{userEmail}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Notifications and Logout */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            {/* Logout Button */}
            <button
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 16px',
                background: '#fff',
                border: '2px solid #e5e7eb',
                borderRadius: 12,
                cursor: 'pointer',
                transition: 'all 0.2s',
                color: '#6b7280',
                fontWeight: 600,
                fontSize: 14
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = '#ef4444';
                e.currentTarget.style.background = '#fef2f2';
                e.currentTarget.style.color = '#ef4444';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              <FaSignOutAlt size={16} />
              <span>{isArabic ? 'تسجيل الخروج' : 'Logout'}</span>
            </button>

            {/* Notifications */}
            <div style={{ position: 'relative' }} data-notifications>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
              style={{
                position: 'relative',
                padding: '10px',
                background: '#fff',
                border: '2px solid #e5e7eb',
                borderRadius: 12,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = '#1e40af';
                e.currentTarget.style.background = '#f0f9ff';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.background = '#fff';
              }}
            >
              <FaBell size={20} color="#6b7280" />
              {notifications.filter(n => n.unread).length > 0 && (
                <span style={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  background: '#ef4444',
                  color: '#fff',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  fontWeight: 700
                }}>
                  {notifications.filter(n => n.unread).length}
                </span>
              )}
            </button>
            
            {/* Notifications Dropdown */}
            {showNotifications && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: 8,
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                border: '1px solid #e5e7eb',
                width: 360,
                maxHeight: 500,
                overflowY: 'auto',
                zIndex: 1000
              }}>
                <div style={{
                  padding: 16,
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <h3 style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: '#111827',
                    margin: 0
                  }}>
                    {isArabic ? 'الإشعارات' : 'Notifications'}
                  </h3>
                  <button
                    onClick={() => {
                      setNotifications(notifs => notifs.map(n => ({ ...n, unread: false })));
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#1e40af',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    {isArabic ? 'تحديد الكل كمقروء' : 'Mark all read'}
                  </button>
                </div>
                {notifications.length === 0 ? (
                  <div style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: 14
                  }}>
                    {isArabic ? 'لا توجد إشعارات' : 'No notifications'}
                  </div>
                ) : (
                  <div>
                    {notifications.map(notif => (
                      <div
                        key={notif.id}
                        onClick={() => {
                          setActiveTab(notif.link.replace('#', ''));
                          setShowNotifications(false);
                          setNotifications(notifs => 
                            notifs.map(n => n.id === notif.id ? { ...n, unread: false } : n)
                          );
                        }}
                        style={{
                          padding: 16,
                          borderBottom: '1px solid #f3f4f6',
                          cursor: 'pointer',
                          background: notif.unread ? '#f0f9ff' : '#fff',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                        onMouseLeave={e => e.currentTarget.style.background = notif.unread ? '#f0f9ff' : '#fff'}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 12
                        }}>
                          <div style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: notif.unread ? '#3b82f6' : 'transparent',
                            marginTop: 6,
                            flexShrink: 0
                          }} />
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: '#111827',
                              marginBottom: 4
                            }}>
                              {notif.title}
                            </div>
                            <div style={{
                              fontSize: 13,
                              color: '#6b7280',
                              marginBottom: 6
                            }}>
                              {notif.message}
                            </div>
                            <div style={{
                              fontSize: 11,
                              color: '#9ca3af'
                            }}>
                              {new Date(notif.time).toLocaleDateString()} {new Date(notif.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: 8,
          marginBottom: 24,
          borderBottom: '2px solid #e5e7eb',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setActiveTab('overview')}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'overview' ? '3px solid #3b82f6' : '3px solid transparent',
              color: activeTab === 'overview' ? '#3b82f6' : '#6b7280',
              fontWeight: activeTab === 'overview' ? 700 : 600,
              fontSize: 15,
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <FaChartLine size={16} />
            {isArabic ? 'نظرة عامة' : 'Overview'}
          </button>
          <button
            onClick={() => setActiveTab('sessions')}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'sessions' ? '3px solid #3b82f6' : '3px solid transparent',
              color: activeTab === 'sessions' ? '#3b82f6' : '#6b7280',
              fontWeight: activeTab === 'sessions' ? 700 : 600,
              fontSize: 15,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {isArabic ? 'الجلسات' : 'Sessions'}
            <span style={{
              marginLeft: 8,
              background: '#e5e7eb',
              color: '#374151',
              padding: '2px 8px',
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 600
            }}>
              {sessions.filter(s => (s.session_type || 'session') === 'session').length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('courses')}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'courses' ? '3px solid #8b5cf6' : '3px solid transparent',
              color: activeTab === 'courses' ? '#8b5cf6' : '#6b7280',
              fontWeight: activeTab === 'courses' ? 700 : 600,
              fontSize: 15,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {isArabic ? 'الدورات' : 'Courses'}
            <span style={{
              marginLeft: 8,
              background: '#e5e7eb',
              color: '#374151',
              padding: '2px 8px',
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 600
            }}>
              {sessions.filter(s => (s.session_type || 'session') === 'course').length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'requests' ? '3px solid #3b82f6' : '3px solid transparent',
              color: activeTab === 'requests' ? '#3b82f6' : '#6b7280',
              fontWeight: activeTab === 'requests' ? 700 : 600,
              fontSize: 15,
              cursor: 'pointer',
              transition: 'all 0.2s',
              position: 'relative'
            }}
          >
            {isArabic ? 'الطلبات' : 'Requests'}
            {pendingRequests.length > 0 && (
              <span style={{
                marginLeft: 8,
                background: '#ef4444',
                color: '#fff',
                padding: '2px 8px',
                borderRadius: 12,
                fontSize: 12,
                fontWeight: 600
              }}>
                {pendingRequests.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('students')}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'students' ? '3px solid #3b82f6' : '3px solid transparent',
              color: activeTab === 'students' ? '#3b82f6' : '#6b7280',
              fontWeight: activeTab === 'students' ? 700 : 600,
              fontSize: 15,
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <FaGraduationCap size={16} />
            {isArabic ? 'الطلاب' : 'Students'}
            <span style={{
              marginLeft: 8,
              background: '#e5e7eb',
              color: '#374151',
              padding: '2px 8px',
              borderRadius: 12,
              fontSize: 12,
              fontWeight: 600
            }}>
              {students.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'analytics' ? '3px solid #3b82f6' : '3px solid transparent',
              color: activeTab === 'analytics' ? '#3b82f6' : '#6b7280',
              fontWeight: activeTab === 'analytics' ? 700 : 600,
              fontSize: 15,
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <FaChartPie size={16} />
            {isArabic ? 'التحليلات' : 'Analytics'}
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'calendar' ? '3px solid #3b82f6' : '3px solid transparent',
              color: activeTab === 'calendar' ? '#3b82f6' : '#6b7280',
              fontWeight: activeTab === 'calendar' ? 700 : 600,
              fontSize: 15,
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <FaCalendarAlt size={16} />
            {isArabic ? 'التقويم' : 'Calendar'}
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            style={{
              padding: '12px 24px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'settings' ? '3px solid #3b82f6' : '3px solid transparent',
              color: activeTab === 'settings' ? '#3b82f6' : '#6b7280',
              fontWeight: activeTab === 'settings' ? 700 : 600,
              fontSize: 15,
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <FaCog size={16} />
            {isArabic ? 'الإعدادات' : 'Settings'}
          </button>
          {/* Templates, Waitlist tabs removed to simplify UI */}
        </div>

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div>
            {/* Key Metrics Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 20,
              marginBottom: 32
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                borderRadius: 16,
                padding: 24,
                color: '#fff',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
              }}>
                <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 8, fontWeight: 500 }}>
                  {isArabic ? 'طلبات آخر 7 أيام' : 'Requests (7 days)'}
                </div>
                <div style={{ fontSize: 32, fontWeight: 800, marginBottom: 4 }}>
                  {analyticsData.recentRequests}
                </div>
                <div style={{ fontSize: 12, opacity: 0.8 }}>
                  {isArabic ? 'طلب جديد' : 'new requests'}
                </div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: 16,
                padding: 24,
                color: '#fff',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
              }}>
                <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 8, fontWeight: 500 }}>
                  {isArabic ? 'جلسات آخر 7 أيام' : 'Sessions (7 days)'}
                </div>
                <div style={{ fontSize: 32, fontWeight: 800, marginBottom: 4 }}>
                  {analyticsData.recentSessions}
                </div>
                <div style={{ fontSize: 12, opacity: 0.8 }}>
                  {isArabic ? 'جلسة مجدولة' : 'scheduled'}
                </div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                borderRadius: 16,
                padding: 24,
                color: '#fff',
                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
              }}>
                <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 8, fontWeight: 500 }}>
                  {isArabic ? 'إيرادات آخر 7 أيام' : 'Revenue (7 days)'}
                </div>
                <div style={{ fontSize: 32, fontWeight: 800, marginBottom: 4 }}>
                  {analyticsData.recentRevenue.toFixed(2)}
                </div>
                <div style={{ fontSize: 12, opacity: 0.8 }}>
                  {isArabic ? 'دينار بحريني' : 'BD'}
                </div>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                borderRadius: 16,
                padding: 24,
                color: '#fff',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
              }}>
                <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 8, fontWeight: 500 }}>
                  {isArabic ? 'معدل القبول' : 'Acceptance Rate'}
                </div>
                <div style={{ fontSize: 32, fontWeight: 800, marginBottom: 4 }}>
                  {analyticsData.acceptanceRate}%
                </div>
                <div style={{ fontSize: 12, opacity: 0.8 }}>
                  {isArabic ? 'من إجمالي الطلبات' : 'of total requests'}
                </div>
              </div>
            </div>

            {/* Platform Schools Overview Card */}
            {schoolStats.total > 0 && (
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 16,
                padding: 24,
                marginBottom: 24,
                color: '#fff',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                  <div>
                    <h3 style={{ margin: '0 0 8px 0', fontSize: 20, fontWeight: 700, color: '#fff' }}>
                      {isArabic ? 'أهم المدارس والجامعات في المنصة' : 'Top Schools & Universities on Platform'}
                    </h3>
                    <p style={{ margin: 0, fontSize: 14, opacity: 0.9 }}>
                      {isArabic 
                        ? `إجمالي ${schoolStats.total} طالب مسجل من مختلف المدارس والجامعات`
                        : `Total ${schoolStats.total} registered students from various schools & universities`}
                    </p>
                  </div>
                  <FaGraduationCap size={32} style={{ opacity: 0.8 }} />
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                  gap: 16
                }}>
                  {schoolStats.items.slice(0, 3).map((item, idx) => {
                    const rank = idx + 1;
                    const displayName = item.name === 'Unknown' ? (isArabic ? 'غير محدد' : 'Unknown / Other') : item.name;
                    return (
                      <div key={item.name || idx} style={{
                        background: 'rgba(255, 255, 255, 0.15)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: 12,
                        padding: 16,
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                          <div style={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            background: '#fff',
                            color: '#667eea',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 14,
                            fontWeight: 700
                          }}>
                            {rank}
                          </div>
                          <span style={{ fontWeight: 700, fontSize: 15, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {displayName}
                          </span>
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 4 }}>
                          {item.percent}%
                        </div>
                        <div style={{ fontSize: 12, opacity: 0.9 }}>
                          {item.count} {isArabic ? 'طالب' : 'students'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Charts Row 1 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: 24,
              marginBottom: 24
            }}>
              {/* Requests Over Time Chart */}
              <div style={{
                background: '#fff',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{ margin: '0 0 20px 0', fontSize: 18, fontWeight: 700, color: '#111827' }}>
                  {isArabic ? 'طلبات الطلاب (آخر 30 يومًا)' : 'Student Requests (Last 30 Days)'}
                </h3>
                {analyticsData.requestsOverTime.length === 0 || analyticsData.requestsOverTime.every(d => d.count === 0) ? (
                  <p style={{ fontSize: 14, color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
                    {isArabic ? 'لا توجد بيانات' : 'No data available'}
                  </p>
                ) : (
                  <div style={{ height: 200, display: 'flex', alignItems: 'flex-end', gap: 4, paddingTop: 20 }}>
                    {analyticsData.requestsOverTime.map((data, idx) => {
                      const maxCount = Math.max(...analyticsData.requestsOverTime.map(d => d.count), 1);
                      const height = maxCount > 0 ? (data.count / maxCount) * 100 : 0;
                      return (
                        <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                          <div style={{
                            width: '100%',
                            height: `${height}%`,
                            minHeight: height > 0 ? 4 : 0,
                            background: 'linear-gradient(180deg, #3b82f6, #2563eb)',
                            borderRadius: '4px 4px 0 0',
                            transition: 'height 0.3s ease',
                            cursor: 'pointer',
                            position: 'relative'
                          }}>
                            {data.count > 0 && (
                              <div style={{
                                position: 'absolute',
                                top: -28,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: '#111827',
                                color: '#fff',
                                padding: '4px 8px',
                                borderRadius: 4,
                                fontSize: 11,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                zIndex: 10
                              }}>
                                {data.count}
                              </div>
                            )}
                          </div>
                          {idx % 7 === 0 && (
                            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
                              {new Date(data.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Sessions Over Time Chart */}
              <div style={{
                background: '#fff',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{ margin: '0 0 20px 0', fontSize: 18, fontWeight: 700, color: '#111827' }}>
                  {isArabic ? 'الجلسات المجدولة (آخر 30 يومًا)' : 'Scheduled Sessions (Last 30 Days)'}
                </h3>
                {analyticsData.sessionsOverTime.length === 0 || analyticsData.sessionsOverTime.every(d => d.count === 0) ? (
                  <p style={{ fontSize: 14, color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
                    {isArabic ? 'لا توجد بيانات' : 'No data available'}
                  </p>
                ) : (
                  <div style={{ height: 200, display: 'flex', alignItems: 'flex-end', gap: 4, paddingTop: 20 }}>
                    {analyticsData.sessionsOverTime.map((data, idx) => {
                      const maxCount = Math.max(...analyticsData.sessionsOverTime.map(d => d.count), 1);
                      const height = maxCount > 0 ? (data.count / maxCount) * 100 : 0;
                      return (
                        <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                          <div style={{
                            width: '100%',
                            height: `${height}%`,
                            minHeight: height > 0 ? 4 : 0,
                            background: 'linear-gradient(180deg, #10b981, #059669)',
                            borderRadius: '4px 4px 0 0',
                            transition: 'height 0.3s ease',
                            cursor: 'pointer'
                          }}>
                            {data.count > 0 && (
                              <div style={{
                                position: 'absolute',
                                top: -28,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: '#111827',
                                color: '#fff',
                                padding: '4px 8px',
                                borderRadius: 4,
                                fontSize: 11,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                zIndex: 10
                              }}>
                                {data.count}
                              </div>
                            )}
                          </div>
                          {idx % 7 === 0 && (
                            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
                              {new Date(data.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Charts Row 2 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: 24,
              marginBottom: 24
            }}>
              {/* Request Status Distribution */}
              <div style={{
                background: '#fff',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>
                    {isArabic ? 'حالة طلبات الطلاب' : 'Request Status Distribution'}
                  </h3>
                  <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
                    {requestStatusStats.total || 0} {isArabic ? 'طلب' : 'total'}
                  </span>
                </div>

                {requestStatusStats.total === 0 ? (
                  <p style={{ fontSize: 14, color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
                    {isArabic
                      ? 'لا توجد طلبات حتى الآن'
                      : 'No requests yet'}
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {requestStatusStats.items.map((item) => (
                      <div key={item.key}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14, marginBottom: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              background: item.color
                            }} />
                            <span style={{ fontWeight: 600, color: '#374151' }}>
                              {isArabic ? item.labelAr : item.labelEn}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ color: '#111827', fontWeight: 700, fontSize: 16 }}>
                              {item.count}
                            </span>
                            <span style={{ color: '#6b7280', fontSize: 13 }}>
                              ({item.percent}%)
                            </span>
                          </div>
                        </div>
                        <div style={{
                          width: '100%',
                          height: 12,
                          borderRadius: 999,
                          background: '#e5e7eb',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${item.percent}%`,
                            height: '100%',
                            borderRadius: 999,
                            background: item.color,
                            transition: 'width 0.5s ease',
                            boxShadow: `0 0 8px ${item.color}40`
                          }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Session Type & Format Distribution */}
              <div style={{
                background: '#fff',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{ margin: '0 0 20px 0', fontSize: 18, fontWeight: 700, color: '#111827' }}>
                  {isArabic ? 'توزيع الجلسات' : 'Session Distribution'}
                </h3>
                
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 12 }}>
                    {isArabic ? 'نوع الجلسة' : 'Session Type'}
                  </div>
                  {analyticsData.sessionTypeStats.map((item) => (
                    <div key={item.key} style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                        <span style={{ fontWeight: 600, color: '#374151' }}>
                          {isArabic ? item.labelAr : item.labelEn}
                        </span>
                        <span style={{ color: '#6b7280' }}>
                          {item.count} ({item.percent}%)
                        </span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: 10,
                        borderRadius: 999,
                        background: '#e5e7eb',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${item.percent}%`,
                          height: '100%',
                          borderRadius: 999,
                          background: item.color,
                          transition: 'width 0.5s ease'
                        }} />
                      </div>
                    </div>
                  ))}
                </div>

                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#6b7280', marginBottom: 12 }}>
                    {isArabic ? 'صيغة الجلسة' : 'Session Format'}
                  </div>
                  {analyticsData.formatStats.map((item) => (
                    <div key={item.key} style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                        <span style={{ fontWeight: 600, color: '#374151' }}>
                          {isArabic ? item.labelAr : item.labelEn}
                        </span>
                        <span style={{ color: '#6b7280' }}>
                          {item.count} ({item.percent}%)
                        </span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: 10,
                        borderRadius: 999,
                        background: '#e5e7eb',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${item.percent}%`,
                          height: '100%',
                          borderRadius: 999,
                          background: item.color,
                          transition: 'width 0.5s ease'
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Charts Row 3 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
              gap: 24,
              marginBottom: 24
            }}>
              {/* Revenue Over Time */}
              <div style={{
                background: '#fff',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid #e5e7eb'
              }}>
                <h3 style={{ margin: '0 0 20px 0', fontSize: 18, fontWeight: 700, color: '#111827' }}>
                  {isArabic ? 'الإيرادات (آخر 30 يومًا)' : 'Revenue (Last 30 Days)'}
                </h3>
                {analyticsData.revenueOverTime.length === 0 || analyticsData.revenueOverTime.every(d => d.revenue === 0) ? (
                  <p style={{ fontSize: 14, color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
                    {isArabic ? 'لا توجد بيانات' : 'No data available'}
                  </p>
                ) : (
                  <div style={{ height: 200, display: 'flex', alignItems: 'flex-end', gap: 4, paddingTop: 20 }}>
                    {analyticsData.revenueOverTime.map((data, idx) => {
                      const maxRevenue = Math.max(...analyticsData.revenueOverTime.map(d => d.revenue), 1);
                      const height = maxRevenue > 0 ? (data.revenue / maxRevenue) * 100 : 0;
                      return (
                        <div key={idx} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                          <div style={{
                            width: '100%',
                            height: `${height}%`,
                            minHeight: height > 0 ? 4 : 0,
                            background: 'linear-gradient(180deg, #8b5cf6, #7c3aed)',
                            borderRadius: '4px 4px 0 0',
                            transition: 'height 0.3s ease',
                            cursor: 'pointer'
                          }}>
                            {data.revenue > 0 && (
                              <div style={{
                                position: 'absolute',
                                top: -28,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: '#111827',
                                color: '#fff',
                                padding: '4px 8px',
                                borderRadius: 4,
                                fontSize: 11,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                zIndex: 10
                              }}>
                                {data.revenue.toFixed(2)} BD
                              </div>
                            )}
                          </div>
                          {idx % 7 === 0 && (
                            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
                              {new Date(data.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* School / University Distribution - Platform Wide */}
              <div style={{
                background: '#fff',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#111827' }}>
                      {isArabic ? 'المدارس والجامعات في المنصة' : 'Schools & Universities on Platform'}
                    </h3>
                    <span style={{ 
                      fontSize: 11, 
                      color: '#3b82f6', 
                      fontWeight: 600,
                      background: '#eff6ff',
                      padding: '4px 8px',
                      borderRadius: 6
                    }}>
                      {isArabic ? 'جميع الطلاب' : 'All Students'}
                    </span>
                  </div>
                </div>

                {schoolStats.total === 0 ? (
                  <p style={{ fontSize: 14, color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
                    {isArabic
                      ? 'لا توجد بيانات حتى الآن'
                      : 'No data yet'}
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {schoolStats.items.slice(0, 10).map((item, idx) => {
                      const rank = idx + 1;
                      const isTop = rank <= 3;
                      const displayName = item.name === 'Unknown' ? (isArabic ? 'غير محدد' : 'Unknown / Other') : item.name;
                      return (
                        <div key={item.name || idx}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14, marginBottom: 6 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                              <div style={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                background: isTop ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#e5e7eb',
                                color: isTop ? '#fff' : '#374151',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 12,
                                fontWeight: 700,
                                flexShrink: 0
                              }}>
                                {rank}
                              </div>
                              <span style={{ 
                                fontWeight: 600, 
                                color: '#374151',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {displayName}
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                              <span style={{ color: '#6b7280', fontSize: 13, fontWeight: 600 }}>
                                {item.count} {isArabic ? 'طالب' : 'students'}
                              </span>
                              <span style={{ 
                                color: '#111827', 
                                fontWeight: 700, 
                                fontSize: 15,
                                minWidth: '45px',
                                textAlign: 'right'
                              }}>
                                {item.percent}%
                              </span>
                            </div>
                          </div>
                          <div style={{
                            width: '100%',
                            height: 12,
                            borderRadius: 999,
                            background: '#e5e7eb',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${item.percent}%`,
                              height: '100%',
                              borderRadius: 999,
                              background: isTop
                                ? 'linear-gradient(90deg, #3b82f6, #6366f1)'
                                : 'linear-gradient(90deg, #bfdbfe, #93c5fd)',
                              transition: 'width 0.5s ease',
                              boxShadow: isTop ? '0 0 8px rgba(59, 130, 246, 0.3)' : 'none'
                            }} />
                          </div>
                        </div>
                      );
                    })}
                    {schoolStats.items.length > 10 && (
                      <div style={{
                        padding: '12px',
                        background: '#f9fafb',
                        borderRadius: 8,
                        textAlign: 'center',
                        fontSize: 13,
                        color: '#6b7280',
                        fontWeight: 600
                      }}>
                        {isArabic 
                          ? `و ${schoolStats.items.length - 10} مدرسة/جامعة أخرى`
                          : `+ ${schoolStats.items.length - 10} more schools/universities`}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            {/* Statistics Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 20,
              marginBottom: 32
            }}>
              <div style={{
                background: '#fff',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 12
                }}>
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <FaCalendarAlt size={24} color="#fff" />
                  </div>
                </div>
                <div style={{
                  fontSize: 32,
                  fontWeight: 800,
                  color: '#111827',
                  marginBottom: 4
                }}>
                  {stats.totalSessions}
                </div>
                <div style={{
                  fontSize: 14,
                  color: '#6b7280',
                  fontWeight: 600
                }}>
                  {isArabic ? 'إجمالي الجلسات' : 'Total Sessions'}
                </div>
              </div>

              <div style={{
                background: '#fff',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 12
                }}>
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <FaGraduationCap size={24} color="#fff" />
                  </div>
                </div>
                <div style={{
                  fontSize: 32,
                  fontWeight: 800,
                  color: '#111827',
                  marginBottom: 4
                }}>
                  {stats.totalStudents}
                </div>
                <div style={{
                  fontSize: 14,
                  color: '#6b7280',
                  fontWeight: 600
                }}>
                  {isArabic ? 'إجمالي الطلاب' : 'Total Students'}
                </div>
              </div>

              <div style={{
                background: '#fff',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 12
                }}>
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <FaExclamationCircle size={24} color="#fff" />
                  </div>
                </div>
                <div style={{
                  fontSize: 32,
                  fontWeight: 800,
                  color: '#111827',
                  marginBottom: 4
                }}>
                  {stats.pendingRequests}
                </div>
                <div style={{
                  fontSize: 14,
                  color: '#6b7280',
                  fontWeight: 600
                }}>
                  {isArabic ? 'الطلبات المعلقة' : 'Pending Requests'}
                </div>
              </div>

              <div style={{
                background: '#fff',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 12
                }}>
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <FaDollarSign size={24} color="#fff" />
                  </div>
                </div>
                <div style={{
                  fontSize: 32,
                  fontWeight: 800,
                  color: '#111827',
                  marginBottom: 4
                }}>
                  {stats.totalRevenue.toFixed(2)} BD
                </div>
                <div style={{
                  fontSize: 14,
                  color: '#6b7280',
                  fontWeight: 600
                }}>
                  {isArabic ? 'إجمالي الإيرادات' : 'Total Revenue'}
                </div>
              </div>
            </div>

            {/* Additional Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 16,
              marginBottom: 32
            }}>
              <div style={{
                background: '#fff',
                borderRadius: 12,
                padding: 20,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: '#111827',
                  marginBottom: 4
                }}>
                  {stats.upcomingSessions}
                </div>
                <div style={{
                  fontSize: 13,
                  color: '#6b7280'
                }}>
                  {isArabic ? 'جلسات قادمة' : 'Upcoming Sessions'}
                </div>
              </div>
              <div style={{
                background: '#fff',
                borderRadius: 12,
                padding: 20,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: '#111827',
                  marginBottom: 4
                }}>
                  {stats.completedSessions}
                </div>
                <div style={{
                  fontSize: 13,
                  color: '#6b7280'
                }}>
                  {isArabic ? 'جلسات مكتملة' : 'Completed Sessions'}
                </div>
              </div>
              <div style={{
                background: '#fff',
                borderRadius: 12,
                padding: 20,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: '#111827',
                  marginBottom: 4
                }}>
                  {stats.acceptedRequests}
                </div>
                <div style={{
                  fontSize: 13,
                  color: '#6b7280'
                }}>
                  {isArabic ? 'طلبات مقبولة' : 'Accepted Requests'}
                </div>
              </div>
            </div>

            {/* Upcoming Sessions List */}
            <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: 24,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              border: '1px solid #e5e7eb',
              marginBottom: 24
            }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 16, marginTop: 0 }}>
                {isArabic ? 'الجلسات القادمة' : 'Upcoming Sessions'}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {sessions
                  .filter(s => {
                    const sessionDate = new Date(s.date);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return sessionDate >= today && (s.session_type || 'session') === 'session';
                  })
                  .sort((a, b) => {
                    const dateA = new Date(`${a.date} ${a.time_from}`);
                    const dateB = new Date(`${b.date} ${b.time_from}`);
                    return dateA - dateB;
                  })
                  .slice(0, 5)
                  .map(session => (
                    <div
                      key={session.id}
                      onClick={() => {
                        setActiveTab('sessions');
                        setSearchQuery(session.title);
                      }}
                      style={{
                        background: '#f9fafb',
                        borderRadius: 12,
                        padding: 16,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#3b82f6';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(59,130,246,0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontSize: 15,
                            fontWeight: 700,
                            color: '#111827',
                            marginBottom: 4
                          }}>
                            {session.title}
                          </div>
                          <div style={{
                            fontSize: 13,
                            color: '#6b7280',
                            display: 'flex',
                            gap: 12,
                            flexWrap: 'wrap'
                          }}>
                            <span>
                              <FaCalendarAlt size={12} style={{ display: 'inline', marginRight: 4 }} />
                              {formatDate(session.date)}
                            </span>
                            <span>
                              <FaClock size={12} style={{ display: 'inline', marginRight: 4 }} />
                              {formatTime(session.time_from)} - {formatTime(session.time_to)}
                            </span>
                            <span>
                              <FaUsers size={12} style={{ display: 'inline', marginRight: 4 }} />
                              {session.current_students || 0} / {session.max_students}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                {sessions.filter(s => {
                  const sessionDate = new Date(s.date);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return sessionDate >= today && (s.session_type || 'session') === 'session';
                }).length === 0 && (
                  <p style={{ fontSize: 14, color: '#6b7280', textAlign: 'center', padding: '20px 0' }}>
                    {isArabic ? 'لا توجد جلسات قادمة' : 'No upcoming sessions'}
                  </p>
                )}
              </div>
            </div>

            {/* Upcoming Courses List */}
            <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: 24,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 16, marginTop: 0 }}>
                {isArabic ? 'الدورات القادمة' : 'Upcoming Courses'}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {sessions
                  .filter(s => {
                    const sessionDate = new Date(s.date);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return sessionDate >= today && (s.session_type || 'session') === 'course';
                  })
                  .sort((a, b) => {
                    const dateA = new Date(`${a.date} ${a.time_from}`);
                    const dateB = new Date(`${b.date} ${b.time_from}`);
                    return dateA - dateB;
                  })
                  .slice(0, 5)
                  .map(session => (
                    <div
                      key={session.id}
                      onClick={() => {
                        setActiveTab('sessions');
                        setSearchQuery(session.title);
                      }}
                      style={{
                        background: '#f9fafb',
                        borderRadius: 12,
                        padding: 16,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#8b5cf6';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(139,92,246,0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontSize: 15,
                            fontWeight: 700,
                            color: '#111827',
                            marginBottom: 4
                          }}>
                            {session.title}
                          </div>
                          <div style={{
                            fontSize: 13,
                            color: '#6b7280',
                            display: 'flex',
                            gap: 12,
                            flexWrap: 'wrap'
                          }}>
                            <span>
                              <FaCalendarAlt size={12} style={{ display: 'inline', marginRight: 4 }} />
                              {session.start_date && session.end_date 
                                ? formatDateRange(session.start_date, session.end_date)
                                : formatDate(session.date)}
                            </span>
                            <span>
                              <FaClock size={12} style={{ display: 'inline', marginRight: 4 }} />
                              {formatTime(session.time_from)} - {formatTime(session.time_to)}
                            </span>
                            <span>
                              <FaUsers size={12} style={{ display: 'inline', marginRight: 4 }} />
                              {session.current_students || 0} / {session.max_students}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                {sessions.filter(s => {
                  const sessionDate = new Date(s.start_date || s.date);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return sessionDate >= today && (s.session_type || 'session') === 'course';
                }).length === 0 && (
                  <p style={{ fontSize: 14, color: '#6b7280', textAlign: 'center', padding: '20px 0' }}>
                    {isArabic ? 'لا توجد دورات قادمة' : 'No upcoming courses'}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 20,
              flexWrap: 'wrap',
              gap: 12
            }}>
              <h3 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>
                {isArabic ? 'الجلسات المتاحة' : 'Available Sessions'}
              </h3>
              <button
                onClick={() => handleOpenSessionModal()}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                <FaPlus size={14} />
                {isArabic ? 'إضافة جلسة' : 'Add Session'}
              </button>
            </div>

            {/* Search and Filters */}
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 16,
              marginBottom: 20,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                display: 'flex',
                gap: 12,
                flexWrap: 'wrap',
                alignItems: 'center'
              }}>
                {/* Search */}
                <div style={{
                  flex: 1,
                  minWidth: 200,
                  position: 'relative'
                }}>
                  <FaSearch style={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#9ca3af',
                    fontSize: 14
                  }} />
                  <input
                    type="text"
                    placeholder={isArabic ? 'ابحث في الجلسات...' : 'Search sessions...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px 10px 36px',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>

                {/* Type Filter */}
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    style={{
                      padding: '10px 36px 10px 12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: 6,
                      fontSize: 14,
                      fontWeight: 400,
                      background: '#fff',
                      color: '#111827',
                      cursor: 'pointer',
                      outline: 'none',
                      transition: 'all 0.2s',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      width: '100%'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                    }}
                    onMouseEnter={(e) => {
                      if (document.activeElement !== e.target) {
                        e.target.style.borderColor = '#d1d5db';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (document.activeElement !== e.target) {
                        e.target.style.borderColor = '#e5e7eb';
                      }
                    }}
                  >
                    <option value="all">{isArabic ? 'جميع الأنواع' : 'All Types'}</option>
                    <option value="session">{isArabic ? 'جلسة' : 'Session'}</option>
                    <option value="course">{isArabic ? 'دورة' : 'Course'}</option>
                  </select>
                  <FaChevronDown 
                    size={14} 
                    style={{
                      position: 'absolute',
                      right: 12,
                      pointerEvents: 'none',
                      color: '#6b7280'
                    }} 
                  />
                </div>

                {/* Format Filter */}
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <select
                    value={filterFormat}
                    onChange={(e) => setFilterFormat(e.target.value)}
                    style={{
                      padding: '10px 36px 10px 12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: 6,
                      fontSize: 14,
                      fontWeight: 400,
                      background: '#fff',
                      color: '#111827',
                      cursor: 'pointer',
                      outline: 'none',
                      transition: 'all 0.2s',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      width: '100%'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                    }}
                    onMouseEnter={(e) => {
                      if (document.activeElement !== e.target) {
                        e.target.style.borderColor = '#d1d5db';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (document.activeElement !== e.target) {
                        e.target.style.borderColor = '#e5e7eb';
                      }
                    }}
                  >
                    <option value="all">{isArabic ? 'جميع الصيغ' : 'All Formats'}</option>
                    <option value="online">{isArabic ? 'أونلاين' : 'Online'}</option>
                    <option value="in_person">{isArabic ? 'حضوري' : 'In-Person'}</option>
                  </select>
                  <FaChevronDown 
                    size={14} 
                    style={{
                      position: 'absolute',
                      right: 12,
                      pointerEvents: 'none',
                      color: '#6b7280'
                    }} 
                  />
                </div>

                {/* Status Filter */}
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    style={{
                      padding: '10px 36px 10px 12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: 6,
                      fontSize: 14,
                      fontWeight: 400,
                      background: '#fff',
                      color: '#111827',
                      cursor: 'pointer',
                      outline: 'none',
                      transition: 'all 0.2s',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      width: '100%'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                    }}
                    onMouseEnter={(e) => {
                      if (document.activeElement !== e.target) {
                        e.target.style.borderColor = '#d1d5db';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (document.activeElement !== e.target) {
                        e.target.style.borderColor = '#e5e7eb';
                      }
                    }}
                  >
                    <option value="all">{isArabic ? 'جميع الحالات' : 'All Status'}</option>
                    <option value="upcoming">{isArabic ? 'قادمة' : 'Upcoming'}</option>
                    <option value="past">{isArabic ? 'منتهية' : 'Past'}</option>
                    <option value="full">{isArabic ? 'ممتلئة' : 'Full'}</option>
                  </select>
                  <FaChevronDown 
                    size={14} 
                    style={{
                      position: 'absolute',
                      right: 12,
                      pointerEvents: 'none',
                      color: '#6b7280'
                    }} 
                  />
                </div>

                {/* Clear Filters */}
                {(searchQuery || filterType !== 'all' || filterFormat !== 'all' || filterStatus !== 'all') && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setFilterType('all');
                      setFilterFormat('all');
                      setFilterStatus('all');
                    }}
                    style={{
                      padding: '10px 16px',
                      background: '#f3f4f6',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 14,
                      cursor: 'pointer',
                      color: '#374151',
                      fontWeight: 600
                    }}
                  >
                    {isArabic ? 'مسح' : 'Clear'}
                  </button>
                )}
              </div>
            </div>

            {(() => {
              // Filter sessions
              let filteredSessions = sessions;
              
              // Search filter
              if (searchQuery) {
                const query = searchQuery.toLowerCase();
                filteredSessions = filteredSessions.filter(s => 
                  s.title?.toLowerCase().includes(query) ||
                  s.subject?.toLowerCase().includes(query) ||
                  s.teacher_name?.toLowerCase().includes(query) ||
                  s.description?.toLowerCase().includes(query)
                );
              }
              
              // Type filter
              if (filterType !== 'all') {
                filteredSessions = filteredSessions.filter(s => s.session_type === filterType);
              }
              
              // Format filter
              if (filterFormat !== 'all') {
                filteredSessions = filteredSessions.filter(s => s.format === filterFormat);
              }
              
              // Status filter
              if (filterStatus !== 'all') {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                filteredSessions = filteredSessions.filter(s => {
                  const sessionDate = new Date(s.date);
                  sessionDate.setHours(0, 0, 0, 0);
                  
                  if (filterStatus === 'upcoming') {
                    return sessionDate >= today;
                  } else if (filterStatus === 'past') {
                    return sessionDate < today;
                  } else if (filterStatus === 'full') {
                    return (s.current_students || 0) >= (s.max_students || 0);
                  }
                  return true;
                });
              }
              
              return filteredSessions.length === 0 ? (
                <div style={{
                  padding: '40px',
                  background: '#fff',
                  borderRadius: 16,
                  textAlign: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}>
                  <div style={{ fontSize: 16, color: '#6b7280' }}>
                    {isArabic ? 'لا توجد جلسات متاحة' : 'No sessions available'}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {filteredSessions.map(session => (
                    <div
                      key={session.id}
                      style={{
                        background: '#fff',
                        borderRadius: 12,
                        padding: 20,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 16
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            marginBottom: 12,
                            flexWrap: 'wrap'
                          }}>
                            <h4 style={{
                              fontSize: 18,
                              fontWeight: 700,
                              color: '#111827',
                              margin: 0
                            }}>
                              {session.title}
                            </h4>
                            {session.subject && (
                              <span style={{
                                background: '#e0f2fe',
                                color: '#0369a1',
                                fontSize: 12,
                                fontWeight: 600,
                                padding: '4px 10px',
                                borderRadius: 6
                              }}>
                                {session.subject}
                              </span>
                            )}
                          </div>
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 16,
                            fontSize: 13,
                            color: '#6b7280',
                            marginBottom: 8
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {session.teacher_image ? (
                                <img
                                  src={session.teacher_image}
                                  alt={session.teacher_name}
                                  style={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    objectFit: 'cover'
                                  }}
                                />
                              ) : (
                                <FaUser size={12} />
                              )}
                              <span>{session.teacher_name}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{
                                background: session.session_type === 'course' ? '#8b5cf6' : '#3b82f6',
                                color: '#fff',
                                fontSize: 10,
                                fontWeight: 600,
                                padding: '2px 8px',
                                borderRadius: 4
                              }}>
                                {session.session_type === 'course' ? (isArabic ? 'دورة' : 'Course') : (isArabic ? 'جلسة' : 'Session')}
                              </span>
                              <span style={{
                                background: session.format === 'online' ? '#10b981' : '#f59e0b',
                                color: '#fff',
                                fontSize: 10,
                                fontWeight: 600,
                                padding: '2px 8px',
                                borderRadius: 4
                              }}>
                                {session.format === 'online' ? (isArabic ? 'أونلاين' : 'Online') : (isArabic ? 'حضوري' : 'In-Person')}
                              </span>
                            </div>
                            {session.format === 'in_person' && session.place && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <FaMapMarkerAlt size={12} />
                                <span>{session.place}</span>
                              </div>
                            )}
                            {session.format === 'online' && session.meeting_link && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <FaUsers size={12} color="#10b981" />
                                <span style={{ color: '#10b981', fontSize: 11 }}>{isArabic ? 'رابط متاح' : 'Link Available'}</span>
                              </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <FaCalendarAlt size={12} />
                              <span>{formatDate(session.date)}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <FaClock size={12} />
                              <span>{formatTime(session.time_from)} - {formatTime(session.time_to)}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <FaUsers size={12} />
                              <span>{session.current_students || 0} / {session.max_students}</span>
                            </div>
                          </div>
                          {session.description && (
                            <p style={{
                              color: '#6b7280',
                              fontSize: 14,
                              marginTop: 8,
                              marginBottom: 0
                            }}>
                              {session.description}
                            </p>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => {
                              // Duplicate session
                              const duplicatedSession = {
                                ...session,
                                id: undefined,
                                date: '',
                                title: `${session.title} (Copy)`
                              };
                              handleOpenSessionModal(duplicatedSession);
                            }}
                            style={{
                              padding: '8px 12px',
                              background: '#e0f2fe',
                              border: 'none',
                              borderRadius: 8,
                              cursor: 'pointer',
                              color: '#0369a1',
                              title: isArabic ? 'نسخ الجلسة' : 'Duplicate Session'
                            }}
                          >
                            <FaCopy size={14} />
                          </button>
                          <button
                            onClick={() => handleOpenSessionModal(session)}
                            style={{
                              padding: '8px 12px',
                              background: '#f3f4f6',
                              border: 'none',
                              borderRadius: 8,
                              cursor: 'pointer',
                              color: '#374151'
                            }}
                          >
                            <FaEdit size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteSession(session.id)}
                            style={{
                              padding: '8px 12px',
                              background: '#fee2e2',
                              border: 'none',
                              borderRadius: 8,
                              cursor: 'pointer',
                              color: '#dc2626'
                            }}
                          >
                            <FaTrash size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* Courses Tab */}
        {activeTab === 'courses' && (
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 20,
              flexWrap: 'wrap',
              gap: 12
            }}>
              <h3 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>
                {isArabic ? 'الدورات المتاحة' : 'Available Courses'}
              </h3>
              <button
                onClick={() => {
                  handleOpenSessionModal({ session_type: 'course' });
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                <FaPlus size={14} />
                {isArabic ? 'إضافة دورة' : 'Add Course'}
              </button>
            </div>

            {/* Search and Filters */}
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 16,
              marginBottom: 20,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{
                display: 'flex',
                gap: 12,
                flexWrap: 'wrap',
                alignItems: 'center'
              }}>
                {/* Search */}
                <div style={{
                  flex: 1,
                  minWidth: 200,
                  position: 'relative'
                }}>
                  <FaSearch style={{
                    position: 'absolute',
                    left: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#9ca3af',
                    fontSize: 14
                  }} />
                  <input
                    type="text"
                    placeholder={isArabic ? 'ابحث في الدورات...' : 'Search courses...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px 10px 36px',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      outline: 'none',
                      transition: 'border-color 0.2s'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                  />
                </div>

                {/* Format Filter */}
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <select
                    value={filterFormat}
                    onChange={(e) => setFilterFormat(e.target.value)}
                    style={{
                      padding: '10px 36px 10px 12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: 6,
                      fontSize: 14,
                      fontWeight: 400,
                      background: '#fff',
                      color: '#111827',
                      cursor: 'pointer',
                      outline: 'none',
                      transition: 'all 0.2s',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      width: '100%'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#8b5cf6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                    }}
                    onMouseEnter={(e) => {
                      if (document.activeElement !== e.target) {
                        e.target.style.borderColor = '#d1d5db';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (document.activeElement !== e.target) {
                        e.target.style.borderColor = '#e5e7eb';
                      }
                    }}
                  >
                    <option value="all">{isArabic ? 'جميع الصيغ' : 'All Formats'}</option>
                    <option value="online">{isArabic ? 'أونلاين' : 'Online'}</option>
                    <option value="in_person">{isArabic ? 'حضوري' : 'In-Person'}</option>
                  </select>
                  <FaChevronDown 
                    size={14} 
                    style={{
                      position: 'absolute',
                      right: 12,
                      pointerEvents: 'none',
                      color: '#6b7280'
                    }} 
                  />
                </div>

                {/* Status Filter */}
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    style={{
                      padding: '10px 36px 10px 12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: 6,
                      fontSize: 14,
                      fontWeight: 400,
                      background: '#fff',
                      color: '#111827',
                      cursor: 'pointer',
                      outline: 'none',
                      transition: 'all 0.2s',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      width: '100%'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = '#8b5cf6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e5e7eb';
                      e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                    }}
                    onMouseEnter={(e) => {
                      if (document.activeElement !== e.target) {
                        e.target.style.borderColor = '#d1d5db';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (document.activeElement !== e.target) {
                        e.target.style.borderColor = '#e5e7eb';
                      }
                    }}
                  >
                    <option value="all">{isArabic ? 'جميع الحالات' : 'All Status'}</option>
                    <option value="upcoming">{isArabic ? 'قادمة' : 'Upcoming'}</option>
                    <option value="past">{isArabic ? 'منتهية' : 'Past'}</option>
                    <option value="full">{isArabic ? 'ممتلئة' : 'Full'}</option>
                  </select>
                  <FaChevronDown 
                    size={14} 
                    style={{
                      position: 'absolute',
                      right: 12,
                      pointerEvents: 'none',
                      color: '#6b7280'
                    }} 
                  />
                </div>

                {/* Clear Filters */}
                {(searchQuery || filterFormat !== 'all' || filterStatus !== 'all') && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setFilterFormat('all');
                      setFilterStatus('all');
                    }}
                    style={{
                      padding: '10px 16px',
                      background: '#f3f4f6',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 14,
                      cursor: 'pointer',
                      color: '#374151',
                      fontWeight: 600
                    }}
                  >
                    {isArabic ? 'مسح' : 'Clear'}
                  </button>
                )}
              </div>
            </div>

            {(() => {
              // Filter courses only
              let filteredCourses = sessions.filter(s => (s.session_type || 'session') === 'course');
              
              // Search filter
              if (searchQuery) {
                const query = searchQuery.toLowerCase();
                filteredCourses = filteredCourses.filter(s => 
                  s.title?.toLowerCase().includes(query) ||
                  s.subject?.toLowerCase().includes(query) ||
                  s.teacher_name?.toLowerCase().includes(query) ||
                  s.description?.toLowerCase().includes(query)
                );
              }
              
              // Format filter
              if (filterFormat !== 'all') {
                filteredCourses = filteredCourses.filter(s => s.format === filterFormat);
              }
              
              // Status filter
              if (filterStatus !== 'all') {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                filteredCourses = filteredCourses.filter(s => {
                  const sessionDate = new Date(s.start_date || s.date);
                  sessionDate.setHours(0, 0, 0, 0);
                  
                  if (filterStatus === 'upcoming') {
                    return sessionDate >= today;
                  } else if (filterStatus === 'past') {
                    const endDate = s.end_date ? new Date(s.end_date) : sessionDate;
                    endDate.setHours(0, 0, 0, 0);
                    return endDate < today;
                  } else if (filterStatus === 'full') {
                    return (s.current_students || 0) >= (s.max_students || 0);
                  }
                  return true;
                });
              }
              
              return filteredCourses.length === 0 ? (
                <div style={{
                  padding: '40px',
                  background: '#fff',
                  borderRadius: 16,
                  textAlign: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}>
                  <div style={{ fontSize: 16, color: '#6b7280' }}>
                    {isArabic ? 'لا توجد دورات متاحة' : 'No courses available'}
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {filteredCourses.map(course => (
                    <div
                      key={course.id}
                      style={{
                        background: '#fff',
                        borderRadius: 12,
                        padding: 20,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 16
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            marginBottom: 12,
                            flexWrap: 'wrap'
                          }}>
                            <h4 style={{
                              fontSize: 18,
                              fontWeight: 700,
                              color: '#111827',
                              margin: 0
                            }}>
                              {course.title}
                            </h4>
                            {course.subject && (
                              <span style={{
                                background: '#f3e8ff',
                                color: '#7c3aed',
                                fontSize: 12,
                                fontWeight: 600,
                                padding: '4px 10px',
                                borderRadius: 6
                              }}>
                                {course.subject}
                              </span>
                            )}
                            <span style={{
                              background: '#8b5cf6',
                              color: '#fff',
                              fontSize: 10,
                              fontWeight: 600,
                              padding: '2px 8px',
                              borderRadius: 4
                            }}>
                              {isArabic ? 'دورة' : 'Course'}
                            </span>
                          </div>
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 16,
                            fontSize: 13,
                            color: '#6b7280',
                            marginBottom: 8
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              {course.teacher_image ? (
                                <img
                                  src={course.teacher_image}
                                  alt={course.teacher_name}
                                  style={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    objectFit: 'cover'
                                  }}
                                />
                              ) : (
                                <FaUser size={12} />
                              )}
                              <span>{course.teacher_name}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{
                                background: course.format === 'online' ? '#10b981' : '#f59e0b',
                                color: '#fff',
                                fontSize: 10,
                                fontWeight: 600,
                                padding: '2px 8px',
                                borderRadius: 4
                              }}>
                                {course.format === 'online' ? (isArabic ? 'أونلاين' : 'Online') : (isArabic ? 'حضوري' : 'In-Person')}
                              </span>
                            </div>
                            {course.format === 'in_person' && course.place && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <FaMapMarkerAlt size={12} />
                                <span>{course.place}</span>
                              </div>
                            )}
                            {course.format === 'online' && course.meeting_link && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <FaUsers size={12} color="#10b981" />
                                <span style={{ color: '#10b981', fontSize: 11 }}>{isArabic ? 'رابط متاح' : 'Link Available'}</span>
                              </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <FaCalendarAlt size={12} />
                              <span>{course.start_date && course.end_date 
                                ? formatDateRange(course.start_date, course.end_date)
                                : formatDate(course.date)}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <FaClock size={12} />
                              <span>{formatTime(course.time_from)} - {formatTime(course.time_to)}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <FaUsers size={12} />
                              <span>{course.current_students || 0} / {course.max_students}</span>
                            </div>
                            {course.price && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <FaDollarSign size={12} />
                                <span style={{ fontWeight: 600, color: '#8b5cf6' }}>{course.price} BD</span>
                              </div>
                            )}
                          </div>
                          {course.description && (
                            <p style={{
                              color: '#6b7280',
                              fontSize: 14,
                              marginTop: 8,
                              marginBottom: 0
                            }}>
                              {course.description}
                            </p>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => {
                              // Duplicate course
                              const duplicatedCourse = {
                                ...course,
                                id: undefined,
                                date: '',
                                title: `${course.title} (Copy)`
                              };
                              handleOpenSessionModal(duplicatedCourse);
                            }}
                            style={{
                              padding: '8px 12px',
                              background: '#f3e8ff',
                              border: 'none',
                              borderRadius: 8,
                              cursor: 'pointer',
                              color: '#7c3aed',
                              title: isArabic ? 'نسخ الدورة' : 'Duplicate Course'
                            }}
                          >
                            <FaCopy size={14} />
                          </button>
                          <button
                            onClick={() => handleOpenSessionModal(course)}
                            style={{
                              padding: '8px 12px',
                              background: '#f3f4f6',
                              border: 'none',
                              borderRadius: 8,
                              cursor: 'pointer',
                              color: '#374151'
                            }}
                          >
                            <FaEdit size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteSession(course.id)}
                            style={{
                              padding: '8px 12px',
                              background: '#fee2e2',
                              border: 'none',
                              borderRadius: 8,
                              cursor: 'pointer',
                              color: '#dc2626'
                            }}
                          >
                            <FaTrash size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 20
            }}>
              <h3 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>
                {isArabic ? 'قائمة الطلاب' : 'Students List'}
              </h3>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12
              }}>
                <input
                  type="text"
                  placeholder={isArabic ? 'ابحث عن طالب...' : 'Search student...'}
                  style={{
                    padding: '10px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: 10,
                    fontSize: 14,
                    width: 250
                  }}
                />
              </div>
            </div>

            {students.length === 0 ? (
              <div style={{
                padding: '40px',
                background: '#fff',
                borderRadius: 16,
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}>
                <FaGraduationCap size={64} color="#d1d5db" style={{ marginBottom: 16 }} />
                <div style={{ fontSize: 16, color: '#6b7280' }}>
                  {isArabic ? 'لا يوجد طلاب بعد' : 'No students yet'}
                </div>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))',
                gap: 20
              }}>
                {students.map((student, idx) => (
                  <div
                    key={idx}
                    style={{
                      background: '#fff',
                      borderRadius: 16,
                      padding: 20,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                      border: '1px solid #e5e7eb',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      marginBottom: 16
                    }}>
                      <div style={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 18
                      }}>
                        {student.name.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: '#111827',
                          margin: '0 0 4px 0'
                        }}>
                          {student.name}
                        </h4>
                        <div style={{
                          fontSize: 13,
                          color: '#6b7280'
                        }}>
                          {student.email}
                        </div>
                      </div>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                      marginBottom: 16
                    }}>
                      {student.phone && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          fontSize: 13,
                          color: '#6b7280'
                        }}>
                          <FaPhone size={12} />
                          <span>{student.phone}</span>
                        </div>
                      )}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 13,
                        color: '#6b7280'
                      }}>
                        <FaEnvelope size={12} />
                        <span>{student.email}</span>
                      </div>
                    </div>

                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: 8,
                      padding: 12,
                      background: '#f9fafb',
                      borderRadius: 10,
                      marginBottom: 16
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: '#111827'
                        }}>
                          {student.totalRequests}
                        </div>
                        <div style={{
                          fontSize: 11,
                          color: '#6b7280'
                        }}>
                          {isArabic ? 'طلبات' : 'Requests'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: '#10b981'
                        }}>
                          {student.acceptedRequests}
                        </div>
                        <div style={{
                          fontSize: 11,
                          color: '#6b7280'
                        }}>
                          {isArabic ? 'مقبول' : 'Accepted'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: '#f59e0b'
                        }}>
                          {student.pendingRequests}
                        </div>
                        <div style={{
                          fontSize: 11,
                          color: '#6b7280'
                        }}>
                          {isArabic ? 'معلق' : 'Pending'}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => {
                        setActiveTab('requests');
                        // Could filter requests by student email
                      }}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: '#f3f4f6',
                        color: '#374151',
                        border: 'none',
                        borderRadius: 8,
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = '#e5e7eb';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = '#f3f4f6';
                      }}
                    >
                      {isArabic ? 'عرض الطلبات' : 'View Requests'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Requests Tab */}
        {activeTab === 'requests' && (
          <div>
            <div style={{
              display: 'flex',
              gap: 12,
              marginBottom: 20,
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => setActiveTab('requests')}
                style={{
                  padding: '8px 16px',
                  background: '#3b82f6',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer'
                }}
              >
                {isArabic ? 'المعلقة' : 'Pending'} ({pendingRequests.length})
              </button>
              <button
                onClick={() => setActiveTab('requests')}
                style={{
                  padding: '8px 16px',
                  background: '#10b981',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer'
                }}
              >
                {isArabic ? 'المقبولة' : 'Accepted'} ({acceptedRequests.length})
              </button>
              <button
                onClick={() => setActiveTab('requests')}
                style={{
                  padding: '8px 16px',
                  background: '#ef4444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer'
                }}
              >
                {isArabic ? 'المرفوضة' : 'Declined'} ({declinedRequests.length})
              </button>
            </div>

            {pendingRequests.length === 0 && acceptedRequests.length === 0 && declinedRequests.length === 0 ? (
              <div style={{
                padding: '40px',
                background: '#fff',
                borderRadius: 16,
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}>
                <div style={{ fontSize: 16, color: '#6b7280' }}>
                  {isArabic ? 'لا توجد طلبات' : 'No requests'}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[...pendingRequests, ...acceptedRequests, ...declinedRequests].map(request => (
                  <div
                    key={request.id}
                    style={{
                      background: '#fff',
                      borderRadius: 12,
                      padding: 20,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                      border: '1px solid #e5e7eb',
                      borderLeft: `4px solid ${
                        request.status === 'accepted' ? '#10b981' :
                        request.status === 'declined' ? '#ef4444' : '#f59e0b'
                      }`
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 16
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          marginBottom: 12,
                          flexWrap: 'wrap'
                        }}>
                          <h4 style={{
                            fontSize: 18,
                            fontWeight: 700,
                            color: '#111827',
                            margin: 0
                          }}>
                            {request.title}
                          </h4>
                          <span style={{
                            background: request.status === 'accepted' ? '#d1fae5' :
                                      request.status === 'declined' ? '#fee2e2' : '#fef3c7',
                            color: request.status === 'accepted' ? '#065f46' :
                                   request.status === 'declined' ? '#991b1b' : '#92400e',
                            fontSize: 12,
                            fontWeight: 600,
                            padding: '4px 10px',
                            borderRadius: 6
                          }}>
                            {request.status === 'accepted' ? (isArabic ? 'مقبول' : 'Accepted') :
                             request.status === 'declined' ? (isArabic ? 'مرفوض' : 'Declined') :
                             (isArabic ? 'معلق' : 'Pending')}
                          </span>
                          {request.request_type === 'custom' && (
                            <span style={{
                              background: '#e0e7ff',
                              color: '#3730a3',
                              fontSize: 12,
                              fontWeight: 600,
                              padding: '4px 10px',
                              borderRadius: 6
                            }}>
                              {isArabic ? 'مخصص' : 'Custom'}
                            </span>
                          )}
                        </div>
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 16,
                          fontSize: 13,
                          color: '#6b7280',
                          marginBottom: 12
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <FaUser size={12} />
                            <span>{request.student_name}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <FaEnvelope size={12} />
                            <span>{request.student_email}</span>
                          </div>
                          {request.student_phone && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <FaPhone size={12} />
                              <span>{request.student_phone}</span>
                            </div>
                          )}
                          {request.preferred_date && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <FaCalendarAlt size={12} />
                              <span>{formatDate(request.preferred_date)}</span>
                            </div>
                          )}
                          {request.preferred_time_from && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <FaClock size={12} />
                              <span>{formatTime(request.preferred_time_from)} - {formatTime(request.preferred_time_to)}</span>
                            </div>
                          )}
                          {request.num_students > 1 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <FaUsers size={12} />
                              <span>{request.num_students} {isArabic ? 'طلاب' : 'students'}</span>
                            </div>
                          )}
                        </div>
                        {request.teacher_name && (
                          <div style={{ marginBottom: 8 }}>
                            <strong style={{ fontSize: 13, color: '#374151' }}>
                              {isArabic ? 'المعلم:' : 'Teacher:'} 
                            </strong>
                            <span style={{ fontSize: 13, color: '#6b7280', marginLeft: 8 }}>
                              {request.teacher_name}
                            </span>
                          </div>
                        )}
                        {request.place && (
                          <div style={{ marginBottom: 8 }}>
                            <strong style={{ fontSize: 13, color: '#374151' }}>
                              {isArabic ? 'المكان:' : 'Place:'} 
                            </strong>
                            <span style={{ fontSize: 13, color: '#6b7280', marginLeft: 8 }}>
                              {request.place}
                            </span>
                          </div>
                        )}
                        {request.message && (
                          <div style={{
                            marginTop: 12,
                            padding: 12,
                            background: '#f9fafb',
                            borderRadius: 8,
                            fontSize: 13,
                            color: '#374151',
                            lineHeight: 1.6
                          }}>
                            <strong>{isArabic ? 'الرسالة:' : 'Message:'}</strong>
                            <div style={{ marginTop: 4 }}>{request.message}</div>
                          </div>
                        )}
                        {request.student_names && request.student_names.length > 1 && (
                          <div style={{ marginTop: 12 }}>
                            <strong style={{ fontSize: 13, color: '#374151' }}>
                              {isArabic ? 'الطلاب:' : 'Students:'}
                            </strong>
                            <div style={{ marginTop: 4, fontSize: 13, color: '#6b7280' }}>
                              {request.student_names.join(', ')}
                            </div>
                          </div>
                        )}
                      </div>
                      {request.status === 'pending' && (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={() => handleUpdateRequestStatus(request.id, 'accepted')}
                            style={{
                              padding: '8px 16px',
                              background: '#10b981',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 8,
                              cursor: 'pointer',
                              fontWeight: 600,
                              fontSize: 13,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6
                            }}
                          >
                            <FaCheck size={14} />
                            {isArabic ? 'قبول' : 'Accept'}
                          </button>
                          <button
                            onClick={() => handleUpdateRequestStatus(request.id, 'declined')}
                            style={{
                              padding: '8px 16px',
                              background: '#ef4444',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 8,
                              cursor: 'pointer',
                              fontWeight: 600,
                              fontSize: 13,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6
                            }}
                          >
                            <FaTimes size={14} />
                            {isArabic ? 'رفض' : 'Decline'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 20,
              flexWrap: 'wrap',
              gap: 12
            }}>
              <h3 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>
                {isArabic ? 'قوالب الجلسات' : 'Session Templates'}
              </h3>
              <button
                onClick={() => {
                  setEditingTemplate(null);
                  setTemplateForm({
                    name: '',
                    title: '',
                    teacher_name: '',
                    teacher_email: '',
                    teacher_image: '',
                    subject: '',
                    session_type: 'session',
                    format: 'in_person',
                    place: '',
                    time_from: '',
                    time_to: '',
                    max_students: 10,
                    description: '',
                    price: ''
                  });
                  setTemplateModalOpen(true);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: 'pointer'
                }}
              >
                <FaPlus size={14} />
                {isArabic ? 'إنشاء قالب' : 'Create Template'}
              </button>
            </div>

            {templates.length === 0 ? (
              <div style={{
                padding: '40px',
                background: '#fff',
                borderRadius: 16,
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}>
                <div style={{ fontSize: 16, color: '#6b7280', marginBottom: 12 }}>
                  {isArabic ? 'لا توجد قوالب' : 'No templates yet'}
                </div>
                <div style={{ fontSize: 14, color: '#9ca3af' }}>
                  {isArabic ? 'أنشئ قوالب لحفظ إعدادات الجلسات المتكررة' : 'Create templates to save settings for recurring sessions'}
                </div>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: 16
              }}>
                {templates.map(template => (
                  <div
                    key={template.id}
                    style={{
                      background: '#fff',
                      borderRadius: 12,
                      padding: 20,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                      border: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                    }}
                    onClick={() => {
                      // Use template to create session
                      const sessionData = {
                        ...template,
                        id: undefined,
                        name: undefined,
                        date: '',
                        meeting_link: template.format === 'online' ? '' : undefined
                      };
                      handleOpenSessionModal(sessionData);
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      marginBottom: 12
                    }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: '#111827',
                          margin: '0 0 8px 0'
                        }}>
                          {template.name || template.title}
                        </h4>
                        <div style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: '#374151',
                          marginBottom: 8
                        }}>
                          {template.title}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingTemplate(template);
                            setTemplateForm({
                              name: template.name || '',
                              title: template.title || '',
                              teacher_name: template.teacher_name || '',
                              teacher_email: template.teacher_email || '',
                              teacher_image: template.teacher_image || '',
                              subject: template.subject || '',
                              session_type: template.session_type || 'session',
                              format: template.format || 'in_person',
                              place: template.place || '',
                              time_from: template.time_from || '',
                              time_to: template.time_to || '',
                              max_students: template.max_students || 10,
                              description: template.description || '',
                              price: template.price || ''
                            });
                            setTemplateModalOpen(true);
                          }}
                          style={{
                            padding: '6px 10px',
                            background: '#f3f4f6',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            color: '#374151'
                          }}
                        >
                          <FaEdit size={12} />
                        </button>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm(isArabic ? 'هل أنت متأكد من حذف هذا القالب؟' : 'Are you sure you want to delete this template?')) {
                              const { error } = await deleteSessionTemplate(template.id);
                              if (error) {
                                alert(isArabic ? 'فشل حذف القالب' : 'Failed to delete template');
                              } else {
                                setTemplates(templates.filter(t => t.id !== template.id));
                              }
                            }
                          }}
                          style={{
                            padding: '6px 10px',
                            background: '#fee2e2',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            color: '#dc2626'
                          }}
                        >
                          <FaTrash size={12} />
                        </button>
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 8,
                      fontSize: 12,
                      color: '#6b7280'
                    }}>
                      {template.subject && (
                        <span style={{
                          background: '#e0f2fe',
                          color: '#0369a1',
                          padding: '4px 8px',
                          borderRadius: 4
                        }}>
                          {template.subject}
                        </span>
                      )}
                      <span style={{
                        background: template.session_type === 'course' ? '#f3e8ff' : '#dbeafe',
                        color: template.session_type === 'course' ? '#7c3aed' : '#1e40af',
                        padding: '4px 8px',
                        borderRadius: 4
                      }}>
                        {template.session_type === 'course' ? (isArabic ? 'دورة' : 'Course') : (isArabic ? 'جلسة' : 'Session')}
                      </span>
                      <span style={{
                        background: template.format === 'online' ? '#d1fae5' : '#fef3c7',
                        color: template.format === 'online' ? '#065f46' : '#92400e',
                        padding: '4px 8px',
                        borderRadius: 4
                      }}>
                        {template.format === 'online' ? (isArabic ? 'أونلاين' : 'Online') : (isArabic ? 'حضوري' : 'In-Person')}
                      </span>
                      {template.time_from && template.time_to && (
                        <span style={{
                          background: '#f3f4f6',
                          color: '#374151',
                          padding: '4px 8px',
                          borderRadius: 4
                        }}>
                          {formatTime(template.time_from)} - {formatTime(template.time_to)}
                        </span>
                      )}
                      {template.max_students && (
                        <span style={{
                          background: '#f3f4f6',
                          color: '#374151',
                          padding: '4px 8px',
                          borderRadius: 4
                        }}>
                          Max: {template.max_students}
                        </span>
                      )}
                    </div>
                    {template.price && (
                      <div style={{
                        marginTop: 12,
                        fontSize: 16,
                        fontWeight: 700,
                        color: '#059669'
                      }}>
                        {isArabic ? 'السعر:' : 'Price:'} {template.price} {isArabic ? 'د.ك' : 'KD'}
                      </div>
                    )}
                    <div style={{
                      marginTop: 12,
                      padding: '8px 12px',
                      background: '#eff6ff',
                      borderRadius: 6,
                      fontSize: 12,
                      color: '#1e40af',
                      textAlign: 'center',
                      fontWeight: 600
                    }}>
                      {isArabic ? 'انقر لاستخدام هذا القالب' : 'Click to use this template'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Waitlist Tab */}
        {activeTab === 'waitlist' && (
          <div>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 20 }}>
              {isArabic ? 'قائمة الانتظار' : 'Waitlist Management'}
            </h3>

            {waitlist.length === 0 ? (
              <div style={{
                padding: '40px',
                background: '#fff',
                borderRadius: 16,
                textAlign: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}>
                <div style={{ fontSize: 16, color: '#6b7280' }}>
                  {isArabic ? 'لا توجد طلبات في قائمة الانتظار' : 'No waitlist entries'}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {waitlist.map(entry => {
                  // Handle both direct session object and nested sessions object
                  const session = entry.sessions || (Array.isArray(entry.sessions) ? entry.sessions[0] : null);
                  return (
                    <div
                      key={entry.id}
                      style={{
                        background: '#fff',
                        borderRadius: 12,
                        padding: 20,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        border: '1px solid #e5e7eb'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 16
                      }}>
                        <div style={{ flex: 1 }}>
                          <h4 style={{
                            fontSize: 16,
                            fontWeight: 700,
                            color: '#111827',
                            margin: '0 0 8px 0'
                          }}>
                            {session?.title || 'Session'}
                          </h4>
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 12,
                            fontSize: 13,
                            color: '#6b7280',
                            marginBottom: 8
                          }}>
                            <div>
                              <strong>{isArabic ? 'الطالب:' : 'Student:'}</strong> {entry.student_name || entry.student_email}
                            </div>
                            {session?.date && (
                              <div>
                                <FaCalendarAlt size={12} style={{ display: 'inline', marginRight: 4 }} />
                                {formatDate(session.date)}
                              </div>
                            )}
                            {session?.time_from && session?.time_to && (
                              <div>
                                <FaClock size={12} style={{ display: 'inline', marginRight: 4 }} />
                                {formatTime(session.time_from)} - {formatTime(session.time_to)}
                              </div>
                            )}
                            <div>
                              <strong>{isArabic ? 'انضم في:' : 'Joined:'}</strong> {new Date(entry.created_at).toLocaleDateString()}
                            </div>
                          </div>
                          {session && (
                            <div style={{
                              display: 'flex',
                              gap: 8,
                              flexWrap: 'wrap'
                            }}>
                              <span style={{
                                background: '#fef3c7',
                                color: '#92400e',
                                fontSize: 11,
                                fontWeight: 600,
                                padding: '4px 8px',
                                borderRadius: 4
                              }}>
                                {session.current_students || 0} / {session.max_students} {isArabic ? 'طلاب' : 'students'}
                              </span>
                              {session.format === 'online' && session.meeting_link && (
                                <span style={{
                                  background: '#d1fae5',
                                  color: '#065f46',
                                  fontSize: 11,
                                  fontWeight: 600,
                                  padding: '4px 8px',
                                  borderRadius: 4
                                }}>
                                  {isArabic ? 'أونلاين' : 'Online'}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button
                            onClick={async () => {
                              const { error } = await notifyWaitlistStudent(entry.id);
                              if (error) {
                                alert(isArabic ? 'فشل إرسال الإشعار' : 'Failed to send notification');
                              } else {
                                alert(isArabic ? 'تم إرسال الإشعار للطالب' : 'Notification sent to student');
                                // Reload waitlist
                                if (institute?.id) {
                                  const { data } = await getWaitlistForInstitute(institute.id);
                                  if (data) setWaitlist(data);
                                }
                              }
                            }}
                            style={{
                              padding: '8px 16px',
                              background: '#3b82f6',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 8,
                              cursor: 'pointer',
                              fontSize: 13,
                              fontWeight: 600
                            }}
                          >
                            {isArabic ? 'إشعار' : 'Notify'}
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm(isArabic ? 'هل تريد إزالة هذا الطالب من قائمة الانتظار؟' : 'Remove this student from waitlist?')) {
                                const { error } = await removeFromWaitlist(entry.id);
                                if (error) {
                                  alert(isArabic ? 'فشل الإزالة' : 'Failed to remove');
                                } else {
                                  setWaitlist(waitlist.filter(w => w.id !== entry.id));
                                }
                              }
                            }}
                            style={{
                              padding: '8px 16px',
                              background: '#fee2e2',
                              color: '#dc2626',
                              border: 'none',
                              borderRadius: 8,
                              cursor: 'pointer',
                              fontSize: 13,
                              fontWeight: 600
                            }}
                          >
                            {isArabic ? 'إزالة' : 'Remove'}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <div>
            <h3 style={{ fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 20 }}>
              {isArabic ? 'تقويم الجلسات' : 'Session Calendar'}
            </h3>
            
            <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: 24,
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              border: '1px solid #e5e7eb'
            }}>
              {/* Simple Calendar View */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 8,
                marginBottom: 20
              }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} style={{
                    padding: 12,
                    textAlign: 'center',
                    fontWeight: 700,
                    color: '#6b7280',
                    fontSize: 12
                  }}>
                    {isArabic ? ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'][['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(day)] : day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 8
              }}>
                {(() => {
                  const today = new Date();
                  const currentMonth = today.getMonth();
                  const currentYear = today.getFullYear();
                  const firstDay = new Date(currentYear, currentMonth, 1);
                  const lastDay = new Date(currentYear, currentMonth + 1, 0);
                  const daysInMonth = lastDay.getDate();
                  const startingDayOfWeek = firstDay.getDay();
                  
                  const calendarDays = [];
                  
                  // Empty cells for days before month starts
                  for (let i = 0; i < startingDayOfWeek; i++) {
                    calendarDays.push(null);
                  }
                  
                  // Days of the month
                  for (let day = 1; day <= daysInMonth; day++) {
                    const date = new Date(currentYear, currentMonth, day);
                    const dateStr = date.toISOString().split('T')[0];
                    const daySessions = sessions.filter(s => {
                      const sessionDate = new Date(s.date);
                      return sessionDate.toISOString().split('T')[0] === dateStr;
                    });
                    
                    calendarDays.push({
                      day,
                      date: dateStr,
                      sessions: daySessions,
                      isToday: dateStr === today.toISOString().split('T')[0]
                    });
                  }
                  
                  return calendarDays.map((dayData, index) => {
                    if (!dayData) {
                      return <div key={`empty-${index}`} style={{ minHeight: 80 }} />;
                    }
                    
                    return (
                      <div
                        key={dayData.date}
                        onClick={() => {
                          if (dayData.sessions.length > 0) {
                            setSelectedDate(dayData.date);
                            setActiveTab('sessions');
                            setFilterStatus('all');
                            // Filter sessions by date
                            setSearchQuery(dayData.date);
                          }
                        }}
                        style={{
                          minHeight: 80,
                          padding: 8,
                          background: dayData.isToday ? '#eff6ff' : '#fff',
                          border: dayData.isToday ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                          borderRadius: 8,
                          cursor: dayData.sessions.length > 0 ? 'pointer' : 'default',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (dayData.sessions.length > 0) {
                            e.currentTarget.style.background = '#f0f9ff';
                            e.currentTarget.style.borderColor = '#3b82f6';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = dayData.isToday ? '#eff6ff' : '#fff';
                          e.currentTarget.style.borderColor = dayData.isToday ? '#3b82f6' : '#e5e7eb';
                        }}
                      >
                        <div style={{
                          fontSize: 14,
                          fontWeight: dayData.isToday ? 700 : 600,
                          color: dayData.isToday ? '#3b82f6' : '#111827',
                          marginBottom: 4
                        }}>
                          {dayData.day}
                        </div>
                        {dayData.sessions.length > 0 && (
                          <div style={{
                            fontSize: 11,
                            color: '#059669',
                            fontWeight: 600
                          }}>
                            {dayData.sessions.length} {isArabic ? 'جلسة' : 'session(s)'}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Upcoming Sessions List */}
            <div style={{ marginTop: 24 }}>
              <h4 style={{ fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 16 }}>
                {isArabic ? 'الجلسات القادمة' : 'Upcoming Sessions'}
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {sessions
                  .filter(s => {
                    const sessionDate = new Date(s.date);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return sessionDate >= today;
                  })
                  .sort((a, b) => {
                    const dateA = new Date(`${a.date} ${a.time_from}`);
                    const dateB = new Date(`${b.date} ${b.time_from}`);
                    return dateA - dateB;
                  })
                  .slice(0, 5)
                  .map(session => (
                    <div
                      key={session.id}
                      onClick={() => {
                        setActiveTab('sessions');
                        setSearchQuery(session.title);
                      }}
                      style={{
                        background: '#fff',
                        borderRadius: 12,
                        padding: 16,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#3b82f6';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(59,130,246,0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontSize: 15,
                            fontWeight: 700,
                            color: '#111827',
                            marginBottom: 4
                          }}>
                            {session.title}
                          </div>
                          <div style={{
                            fontSize: 13,
                            color: '#6b7280',
                            display: 'flex',
                            gap: 12,
                            flexWrap: 'wrap'
                          }}>
                            <span>
                              <FaCalendarAlt size={12} style={{ display: 'inline', marginRight: 4 }} />
                              {formatDate(session.date)}
                            </span>
                            <span>
                              <FaClock size={12} style={{ display: 'inline', marginRight: 4 }} />
                              {formatTime(session.time_from)} - {formatTime(session.time_to)}
                            </span>
                            <span>
                              <FaUsers size={12} style={{ display: 'inline', marginRight: 4 }} />
                              {session.current_students || 0} / {session.max_students}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && institute && (
          <div>
            {/* Success/Error Messages */}
            {settingsSuccess && (
              <div style={{
                padding: '14px 20px',
                background: '#d1fae5',
                color: '#065f46',
                borderRadius: 12,
                marginBottom: 24,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: '1px solid #86efac'
              }}>
                <FaCheckCircle />
                {settingsSuccess}
              </div>
            )}
            {settingsError && (
              <div style={{
                padding: '14px 20px',
                background: '#fee2e2',
                color: '#991b1b',
                borderRadius: 12,
                marginBottom: 24,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                border: '1px solid #fca5a5'
              }}>
                <FaExclamationCircle />
                {settingsError}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: 24 }}>
              {/* Institute Profile Settings */}
              <div style={{
                background: '#fff',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}>
                <h3 style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: '#111827',
                  marginBottom: 20,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10
                }}>
                  <FaSchool size={20} />
                  {isArabic ? 'معلومات المعهد' : 'Institute Profile'}
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* Logo Upload */}
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'شعار المعهد' : 'Institute Logo'}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      {logoPreview && (
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          style={{
                            width: 80,
                            height: 80,
                            borderRadius: 12,
                            objectFit: 'cover',
                            border: '2px solid #e5e7eb'
                          }}
                        />
                      )}
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            
                            setUploadingLogo(true);
                            try {
                              const fileExt = file.name.split('.').pop();
                              const fileName = `institute-${institute.id}-${Date.now()}.${fileExt}`;
                              const filePath = `institute-logos/${fileName}`;

                              const { error: uploadError } = await supabase.storage
                                .from('profile-pictures')
                                .upload(filePath, file);

                              if (uploadError) throw uploadError;

                              const { data: { publicUrl } } = supabase.storage
                                .from('profile-pictures')
                                .getPublicUrl(filePath);

                              setSettingsForm(prev => ({ ...prev, logo_url: publicUrl }));
                              setLogoPreview(publicUrl);
                            } catch (error) {
                              console.error('Error uploading logo:', error);
                              alert(isArabic ? 'فشل رفع الشعار' : 'Failed to upload logo');
                            } finally {
                              setUploadingLogo(false);
                            }
                          }}
                          style={{ display: 'none' }}
                          id="logo-upload"
                        />
                        <label
                          htmlFor="logo-upload"
                          style={{
                            padding: '10px 20px',
                            background: '#3b82f6',
                            color: '#fff',
                            borderRadius: 8,
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: 14,
                            display: 'inline-block'
                          }}
                        >
                          {uploadingLogo 
                            ? (isArabic ? 'جاري الرفع...' : 'Uploading...')
                            : (isArabic ? 'رفع شعار' : 'Upload Logo')
                          }
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Institute Name */}
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'اسم المعهد' : 'Institute Name'} *
                    </label>
                    <input
                      type="text"
                      value={settingsForm.name}
                      onChange={e => setSettingsForm(prev => ({ ...prev, name: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14
                      }}
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'البريد الإلكتروني' : 'Email'} *
                    </label>
                    <input
                      type="email"
                      value={settingsForm.email}
                      onChange={e => setSettingsForm(prev => ({ ...prev, email: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14
                      }}
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'الهاتف' : 'Phone'}
                    </label>
                    <input
                      type="tel"
                      value={settingsForm.phone}
                      onChange={e => setSettingsForm(prev => ({ ...prev, phone: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14
                      }}
                    />
                  </div>

                  {/* Location */}
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'الموقع' : 'Location'}
                    </label>
                    <input
                      type="text"
                      value={settingsForm.location}
                      onChange={e => setSettingsForm(prev => ({ ...prev, location: e.target.value }))}
                      placeholder={isArabic ? 'مثال: المنامة، البحرين' : 'e.g., Manama, Bahrain'}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14
                      }}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'الوصف' : 'Description'}
                    </label>
                    <textarea
                      value={settingsForm.description}
                      onChange={e => setSettingsForm(prev => ({ ...prev, description: e.target.value }))}
                      rows={4}
                      placeholder={isArabic ? 'وصف المعهد...' : 'Describe your institute...'}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14,
                        fontFamily: 'inherit',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  {/* Save Button */}
                  <button
                    onClick={async () => {
                      if (!settingsForm.name || !settingsForm.email) {
                        setSettingsError(isArabic ? 'يرجى إدخال الاسم والبريد الإلكتروني' : 'Please enter name and email');
                        return;
                      }

                      setSavingSettings(true);
                      setSettingsError('');
                      setSettingsSuccess('');

                      try {
                        const { error } = await supabase
                          .from('institutes')
                          .update({
                            name: settingsForm.name,
                            email: settingsForm.email,
                            phone: settingsForm.phone || null,
                            location: settingsForm.location || null,
                            description: settingsForm.description || null,
                            logo_url: settingsForm.logo_url || null,
                            updated_at: new Date().toISOString()
                          })
                          .eq('id', institute.id);

                        if (error) throw error;

                        // Update institute state with new settings
                        const updatedInstitute = { ...institute, ...settingsForm };
                        setInstitute(updatedInstitute);
                        setSettingsSuccess(isArabic ? 'تم حفظ الإعدادات بنجاح' : 'Settings saved successfully');
                        setTimeout(() => setSettingsSuccess(''), 3000);
                      } catch (error) {
                        console.error('Error saving settings:', error);
                        setSettingsError(isArabic ? 'فشل حفظ الإعدادات' : 'Failed to save settings');
                      } finally {
                        setSavingSettings(false);
                      }
                    }}
                    disabled={savingSettings}
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: savingSettings ? '#9ca3af' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 10,
                      fontWeight: 700,
                      fontSize: 15,
                      cursor: savingSettings ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8
                    }}
                  >
                    <FaSave size={16} />
                    {savingSettings 
                      ? (isArabic ? 'جاري الحفظ...' : 'Saving...')
                      : (isArabic ? 'حفظ الإعدادات' : 'Save Settings')
                    }
                  </button>
                </div>
              </div>

              {/* Account Settings */}
              <div style={{
                background: '#fff',
                borderRadius: 16,
                padding: 24,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                height: 'fit-content'
              }}>
                <h3 style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: '#111827',
                  marginBottom: 20,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10
                }}>
                  <FaLock size={20} />
                  {isArabic ? 'إعدادات الحساب' : 'Account Settings'}
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* Change Password */}
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'كلمة المرور القديمة' : 'Old Password'}
                    </label>
                    <input
                      type="password"
                      value={passwordForm.oldPassword}
                      onChange={e => setPasswordForm(prev => ({ ...prev, oldPassword: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'كلمة المرور الجديدة' : 'New Password'}
                    </label>
                    <input
                      type="password"
                      value={passwordForm.newPassword}
                      onChange={e => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'تأكيد كلمة المرور' : 'Confirm Password'}
                    </label>
                    <input
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={e => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14
                      }}
                    />
                  </div>

                  <button
                    onClick={async () => {
                      if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
                        setSettingsError(isArabic ? 'يرجى ملء جميع الحقول' : 'Please fill all fields');
                        return;
                      }

                      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
                        setSettingsError(isArabic ? 'كلمات المرور غير متطابقة' : 'Passwords do not match');
                        return;
                      }

                      if (passwordForm.newPassword.length < 6) {
                        setSettingsError(isArabic ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters');
                        return;
                      }

                      setChangingPassword(true);
                      setSettingsError('');
                      setSettingsSuccess('');

                      try {
                        const { error } = await supabase.auth.updateUser({
                          password: passwordForm.newPassword
                        });

                        if (error) throw error;

                        setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
                        setSettingsSuccess(isArabic ? 'تم تغيير كلمة المرور بنجاح' : 'Password changed successfully');
                        setTimeout(() => setSettingsSuccess(''), 3000);
                      } catch (error) {
                        console.error('Error changing password:', error);
                        setSettingsError(isArabic ? 'فشل تغيير كلمة المرور' : 'Failed to change password');
                      } finally {
                        setChangingPassword(false);
                      }
                    }}
                    disabled={changingPassword}
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: changingPassword ? '#9ca3af' : '#10b981',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 10,
                      fontWeight: 700,
                      fontSize: 15,
                      cursor: changingPassword ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8
                    }}
                  >
                    <FaLock size={16} />
                    {changingPassword 
                      ? (isArabic ? 'جاري التغيير...' : 'Changing...')
                      : (isArabic ? 'تغيير كلمة المرور' : 'Change Password')
                    }
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Session Modal */}
        {sessionModalOpen && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20
          }}
          onClick={() => !submitting && setSessionModalOpen(false)}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 16,
                padding: 24,
                maxWidth: 600,
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
              }}
              onClick={e => e.stopPropagation()}
            >
              <h3 style={{
                fontSize: 20,
                fontWeight: 800,
                color: '#111827',
                marginBottom: 20,
                marginTop: 0
              }}>
                {editingSession 
                  ? (isArabic ? 'تعديل الجلسة' : 'Edit Session')
                  : (isArabic ? 'إضافة جلسة جديدة' : 'Add New Session')
                }
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    {isArabic ? 'عنوان الجلسة' : 'Session Title'} *
                  </label>
                  <input
                    type="text"
                    value={sessionForm.title}
                    onChange={e => setSessionForm(prev => ({ ...prev, title: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14
                    }}
                  />
                </div>

                {/* Session Type and Format */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'النوع' : 'Type'} *
                    </label>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
                      <select
                        value={sessionForm.session_type}
                        onChange={e => setSessionForm(prev => ({ ...prev, session_type: e.target.value }))}
                        disabled={activeTab === 'courses' || activeTab === 'sessions'}
                        style={{
                          width: '100%',
                          padding: '10px 36px 10px 12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: 6,
                          fontSize: 14,
                          fontWeight: 400,
                          background: (activeTab === 'courses' || activeTab === 'sessions') ? '#f3f4f6' : '#fff',
                          color: '#111827',
                          cursor: (activeTab === 'courses' || activeTab === 'sessions') ? 'not-allowed' : 'pointer',
                          opacity: (activeTab === 'courses' || activeTab === 'sessions') ? 0.6 : 1,
                          outline: 'none',
                          transition: 'all 0.2s',
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                        }}
                        onFocus={(e) => {
                          if (!e.target.disabled) {
                            e.target.style.borderColor = '#3b82f6';
                            e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                          }
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                        }}
                        onMouseEnter={(e) => {
                          if (!e.target.disabled && document.activeElement !== e.target) {
                            e.target.style.borderColor = '#d1d5db';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (document.activeElement !== e.target) {
                            e.target.style.borderColor = '#e5e7eb';
                          }
                        }}
                      >
                        <option value="session">{isArabic ? 'جلسة' : 'Session'}</option>
                        <option value="course">{isArabic ? 'دورة' : 'Course'}</option>
                      </select>
                      <FaChevronDown 
                        size={14} 
                        style={{
                          position: 'absolute',
                          right: 12,
                          pointerEvents: 'none',
                          color: (activeTab === 'courses' || activeTab === 'sessions') ? '#9ca3af' : '#6b7280'
                        }} 
                      />
                    </div>
                    {(activeTab === 'courses' || activeTab === 'sessions') && (
                      <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4, marginBottom: 0 }}>
                        {isArabic 
                          ? activeTab === 'courses' ? 'النوع محدد تلقائياً كدورة' : 'النوع محدد تلقائياً كجلسة'
                          : activeTab === 'courses' ? 'Type is automatically set to Course' : 'Type is automatically set to Session'}
                      </p>
                    )}
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'الشكل' : 'Format'} *
                    </label>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
                      <select
                        value={sessionForm.format}
                        onChange={e => {
                          setSessionForm(prev => ({ 
                            ...prev, 
                            format: e.target.value,
                            place: e.target.value === 'online' ? '' : prev.place,
                            meeting_link: e.target.value === 'in_person' ? '' : prev.meeting_link
                          }));
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 36px 10px 12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: 6,
                          fontSize: 14,
                          fontWeight: 400,
                          background: '#fff',
                          color: '#111827',
                          cursor: 'pointer',
                          outline: 'none',
                          transition: 'all 0.2s',
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#3b82f6';
                          e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                        }}
                        onMouseEnter={(e) => {
                          if (document.activeElement !== e.target) {
                            e.target.style.borderColor = '#d1d5db';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (document.activeElement !== e.target) {
                            e.target.style.borderColor = '#e5e7eb';
                          }
                        }}
                      >
                        <option value="in_person">{isArabic ? 'حضوري' : 'In-Person'}</option>
                        <option value="online">{isArabic ? 'أونلاين' : 'Online'}</option>
                      </select>
                      <FaChevronDown 
                        size={14} 
                        style={{
                          position: 'absolute',
                          right: 12,
                          pointerEvents: 'none',
                          color: '#6b7280'
                        }} 
                      />
                    </div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'اسم المعلم' : 'Teacher Name'} *
                    </label>
                    <input
                      type="text"
                      value={sessionForm.teacher_name}
                      onChange={e => setSessionForm(prev => ({ ...prev, teacher_name: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'بريد المعلم' : 'Teacher Email'}
                    </label>
                    <input
                      type="email"
                      value={sessionForm.teacher_email}
                      onChange={e => setSessionForm(prev => ({ ...prev, teacher_email: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14
                      }}
                    />
                  </div>
                </div>

                {/* Teacher Image Upload */}
                <div style={{ marginTop: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    {isArabic ? 'صورة المعلم' : 'Teacher Image'}
                  </label>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                    alignItems: 'flex-start'
                  }}>
                    {teacherImagePreview ? (
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <img
                          src={teacherImagePreview}
                          alt="Teacher"
                          style={{
                            width: 120,
                            height: 120,
                            borderRadius: 12,
                            objectFit: 'cover',
                            border: '2px solid #e5e7eb'
                          }}
                        />
                        <button
                          type="button"
                          onClick={handleRemoveTeacherImage}
                          style={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            background: '#ef4444',
                            color: '#fff',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                          }}
                        >
                          <FaTimes size={12} />
                        </button>
                      </div>
                    ) : (
                      <div style={{
                        width: 120,
                        height: 120,
                        borderRadius: 12,
                        border: '2px dashed #d1d5db',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f9fafb',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = '#3b82f6';
                        e.currentTarget.style.background = '#f0f9ff';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.background = '#f9fafb';
                      }}
                      onClick={() => document.getElementById('teacher-image-upload').click()}
                    >
                      <FaImage size={32} color="#9ca3af" />
                    </div>
                    )}
                    <input
                      id="teacher-image-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleTeacherImageUpload}
                      style={{ display: 'none' }}
                      disabled={uploadingImage}
                    />
                    <label
                      htmlFor="teacher-image-upload"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '10px 16px',
                        background: uploadingImage ? '#e5e7eb' : '#f3f4f6',
                        color: uploadingImage ? '#9ca3af' : '#374151',
                        border: 'none',
                        borderRadius: 8,
                        fontWeight: 600,
                        fontSize: 13,
                        cursor: uploadingImage ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => {
                        if (!uploadingImage) {
                          e.currentTarget.style.background = '#e5e7eb';
                        }
                      }}
                      onMouseLeave={e => {
                        if (!uploadingImage) {
                          e.currentTarget.style.background = '#f3f4f6';
                        }
                      }}
                    >
                      {uploadingImage ? (
                        <>
                          <div style={{
                            width: 14,
                            height: 14,
                            border: '2px solid #9ca3af',
                            borderTopColor: 'transparent',
                            borderRadius: '50%',
                            animation: 'spin 0.6s linear infinite'
                          }} />
                          {isArabic ? 'جاري الرفع...' : 'Uploading...'}
                        </>
                      ) : (
                        <>
                          <FaUpload size={14} />
                          {teacherImagePreview 
                            ? (isArabic ? 'تغيير الصورة' : 'Change Image')
                            : (isArabic ? 'رفع صورة المعلم' : 'Upload Teacher Image')
                          }
                        </>
                      )}
                    </label>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'المادة' : 'Subject'}
                    </label>
                    <input
                      type="text"
                      value={sessionForm.subject}
                      onChange={e => setSessionForm(prev => ({ ...prev, subject: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14
                      }}
                    />
                  </div>
                  {sessionForm.format === 'in_person' ? (
                    <div>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                        {isArabic ? 'المكان' : 'Place'} *
                      </label>
                      <input
                        type="text"
                        value={sessionForm.place}
                        onChange={e => setSessionForm(prev => ({ ...prev, place: e.target.value }))}
                        placeholder={isArabic ? 'مثال: المعهد، الطابق الأول' : 'e.g., Institute, First Floor'}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '2px solid #e5e7eb',
                          borderRadius: 8,
                          fontSize: 14
                        }}
                      />
                    </div>
                  ) : (
                    <div>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                        {isArabic ? 'رابط الاجتماع' : 'Meeting Link'}
                      </label>
                      <input
                        type="text"
                        value={sessionForm.meeting_link}
                        onChange={e => setSessionForm(prev => ({ ...prev, meeting_link: e.target.value }))}
                        placeholder={isArabic ? 'سيتم إنشاء الرابط تلقائياً' : 'Will be generated automatically'}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '2px solid #e5e7eb',
                          borderRadius: 8,
                          fontSize: 14,
                          background: sessionForm.meeting_link ? '#fff' : '#f9fafb'
                        }}
                        disabled={!!sessionForm.meeting_link}
                      />
                      {sessionForm.meeting_link && (
                        <div style={{
                          marginTop: 8,
                          padding: 8,
                          background: '#f0f9ff',
                          borderRadius: 6,
                          fontSize: 12,
                          color: '#0369a1'
                        }}>
                          {isArabic ? 'سيتم إرسال هذا الرابط للطلاب بعد الدفع' : 'This link will be sent to students after payment'}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Date/Time Fields - Different for Sessions vs Courses */}
                {sessionForm.session_type === 'course' ? (
                  // Course: Date Range (Start Date - End Date)
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                          {isArabic ? 'تاريخ البداية' : 'Start Date'} *
                        </label>
                        <input
                          type="date"
                          value={sessionForm.start_date}
                          onChange={e => setSessionForm(prev => ({ ...prev, start_date: e.target.value }))}
                          min={new Date().toISOString().split('T')[0]}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid #e5e7eb',
                            borderRadius: 8,
                            fontSize: 14
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                          {isArabic ? 'تاريخ النهاية' : 'End Date'} *
                        </label>
                        <input
                          type="date"
                          value={sessionForm.end_date}
                          onChange={e => setSessionForm(prev => ({ ...prev, end_date: e.target.value }))}
                          min={sessionForm.start_date || new Date().toISOString().split('T')[0]}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid #e5e7eb',
                            borderRadius: 8,
                            fontSize: 14
                          }}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                          {isArabic ? 'وقت البداية' : 'Start Time'} *
                        </label>
                        <input
                          type="time"
                          value={sessionForm.time_from}
                          onChange={e => setSessionForm(prev => ({ ...prev, time_from: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid #e5e7eb',
                            borderRadius: 8,
                            fontSize: 14
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                          {isArabic ? 'وقت النهاية' : 'End Time'} *
                        </label>
                        <input
                          type="time"
                          value={sessionForm.time_to}
                          onChange={e => setSessionForm(prev => ({ ...prev, time_to: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '2px solid #e5e7eb',
                            borderRadius: 8,
                            fontSize: 14
                          }}
                        />
                      </div>
                    </div>
                    {sessionForm.start_date && sessionForm.end_date && (
                      <div style={{
                        padding: '12px',
                        background: '#f0f9ff',
                        borderRadius: 8,
                        border: '1px solid #bae6fd',
                        fontSize: 13,
                        color: '#0369a1'
                      }}>
                        {isArabic 
                          ? `مدة الدورة: ${new Date(sessionForm.end_date) - new Date(sessionForm.start_date) >= 0 ? Math.ceil((new Date(sessionForm.end_date) - new Date(sessionForm.start_date)) / (1000 * 60 * 60 * 24)) + 1 : 1} يوم`
                          : `Course Duration: ${new Date(sessionForm.end_date) - new Date(sessionForm.start_date) >= 0 ? Math.ceil((new Date(sessionForm.end_date) - new Date(sessionForm.start_date)) / (1000 * 60 * 60 * 24)) + 1 : 1} day(s)`}
                      </div>
                    )}
                  </div>
                ) : (
                  // Session: Single Date
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                        {isArabic ? 'التاريخ' : 'Date'} *
                      </label>
                      <input
                        type="date"
                        value={sessionForm.date}
                        onChange={e => setSessionForm(prev => ({ ...prev, date: e.target.value }))}
                        min={new Date().toISOString().split('T')[0]}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '2px solid #e5e7eb',
                          borderRadius: 8,
                          fontSize: 14
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                        {isArabic ? 'من' : 'From'} *
                      </label>
                      <input
                        type="time"
                        value={sessionForm.time_from}
                        onChange={e => setSessionForm(prev => ({ ...prev, time_from: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '2px solid #e5e7eb',
                          borderRadius: 8,
                          fontSize: 14
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                        {isArabic ? 'إلى' : 'To'} *
                      </label>
                      <input
                        type="time"
                        value={sessionForm.time_to}
                        onChange={e => setSessionForm(prev => ({ ...prev, time_to: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          border: '2px solid #e5e7eb',
                          borderRadius: 8,
                          fontSize: 14
                        }}
                      />
                    </div>
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'الحد الأقصى للطلاب' : 'Max Students'} *
                    </label>
                    <input
                      type="number"
                      value={sessionForm.max_students}
                      onChange={e => setSessionForm(prev => ({ ...prev, max_students: parseInt(e.target.value) || 10 }))}
                      min={1}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'السعر' : 'Price (BD)'}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={sessionForm.price}
                      onChange={e => setSessionForm(prev => ({ ...prev, price: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    {isArabic ? 'الوصف' : 'Description'}
                  </label>
                  <textarea
                    value={sessionForm.description}
                    onChange={e => setSessionForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                  <button
                    onClick={handleSaveSession}
                    disabled={submitting}
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: submitting ? '#9ca3af' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 10,
                      fontWeight: 700,
                      fontSize: 15,
                      cursor: submitting ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {submitting 
                      ? (isArabic ? 'جاري الحفظ...' : 'Saving...')
                      : (isArabic ? 'حفظ' : 'Save')
                    }
                  </button>
                  <button
                    onClick={() => setSessionModalOpen(false)}
                    disabled={submitting}
                    style={{
                      padding: '14px 24px',
                      background: '#f3f4f6',
                      color: '#374151',
                      border: 'none',
                      borderRadius: 10,
                      fontWeight: 700,
                      fontSize: 15,
                      cursor: submitting ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isArabic ? 'إلغاء' : 'Cancel'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Template Modal */}
        {templateModalOpen && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 20
          }}
          onClick={() => !submitting && setTemplateModalOpen(false)}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 16,
                padding: 24,
                maxWidth: 600,
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
              }}
              onClick={e => e.stopPropagation()}
            >
              <h3 style={{
                fontSize: 20,
                fontWeight: 800,
                color: '#111827',
                marginBottom: 20,
                marginTop: 0
              }}>
                {editingTemplate 
                  ? (isArabic ? 'تعديل القالب' : 'Edit Template')
                  : (isArabic ? 'إنشاء قالب جديد' : 'Create New Template')
                }
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    {isArabic ? 'اسم القالب' : 'Template Name'} *
                  </label>
                  <input
                    type="text"
                    value={templateForm.name}
                    onChange={e => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={isArabic ? 'مثال: جلسة رياضيات' : 'e.g., Math Tutoring Session'}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    {isArabic ? 'عنوان الجلسة' : 'Session Title'} *
                  </label>
                  <input
                    type="text"
                    value={templateForm.title}
                    onChange={e => setTemplateForm(prev => ({ ...prev, title: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14
                    }}
                  />
                </div>

                {/* Session Type and Format */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'النوع' : 'Type'} *
                    </label>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
                      <select
                        value={templateForm.session_type}
                        onChange={e => setTemplateForm(prev => ({ ...prev, session_type: e.target.value }))}
                        style={{
                          width: '100%',
                          padding: '10px 36px 10px 12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: 6,
                          fontSize: 14,
                          fontWeight: 400,
                          background: '#fff',
                          color: '#111827',
                          cursor: 'pointer',
                          outline: 'none',
                          transition: 'all 0.2s',
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#3b82f6';
                          e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                        }}
                        onMouseEnter={(e) => {
                          if (document.activeElement !== e.target) {
                            e.target.style.borderColor = '#d1d5db';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (document.activeElement !== e.target) {
                            e.target.style.borderColor = '#e5e7eb';
                          }
                        }}
                      >
                        <option value="session">{isArabic ? 'جلسة' : 'Session'}</option>
                        <option value="course">{isArabic ? 'دورة' : 'Course'}</option>
                      </select>
                      <FaChevronDown 
                        size={14} 
                        style={{
                          position: 'absolute',
                          right: 12,
                          pointerEvents: 'none',
                          color: '#6b7280'
                        }} 
                      />
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'الشكل' : 'Format'} *
                    </label>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
                      <select
                        value={templateForm.format}
                        onChange={e => {
                          setTemplateForm(prev => ({ 
                            ...prev, 
                            format: e.target.value,
                            place: e.target.value === 'online' ? '' : prev.place
                          }));
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 36px 10px 12px',
                          border: '1px solid #e5e7eb',
                          borderRadius: 6,
                          fontSize: 14,
                          fontWeight: 400,
                          background: '#fff',
                          color: '#111827',
                          cursor: 'pointer',
                          outline: 'none',
                          transition: 'all 0.2s',
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#3b82f6';
                          e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                        }}
                        onMouseEnter={(e) => {
                          if (document.activeElement !== e.target) {
                            e.target.style.borderColor = '#d1d5db';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (document.activeElement !== e.target) {
                            e.target.style.borderColor = '#e5e7eb';
                          }
                        }}
                      >
                        <option value="in_person">{isArabic ? 'حضوري' : 'In-Person'}</option>
                        <option value="online">{isArabic ? 'أونلاين' : 'Online'}</option>
                      </select>
                      <FaChevronDown 
                        size={14} 
                        style={{
                          position: 'absolute',
                          right: 12,
                          pointerEvents: 'none',
                          color: '#6b7280'
                        }} 
                      />
                    </div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'اسم المعلم' : 'Teacher Name'}
                    </label>
                    <input
                      type="text"
                      value={templateForm.teacher_name}
                      onChange={e => setTemplateForm(prev => ({ ...prev, teacher_name: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'المادة' : 'Subject'}
                    </label>
                    <input
                      type="text"
                      value={templateForm.subject}
                      onChange={e => setTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'من' : 'Time From'}
                    </label>
                    <input
                      type="time"
                      value={templateForm.time_from}
                      onChange={e => setTemplateForm(prev => ({ ...prev, time_from: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'إلى' : 'Time To'}
                    </label>
                    <input
                      type="time"
                      value={templateForm.time_to}
                      onChange={e => setTemplateForm(prev => ({ ...prev, time_to: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14
                      }}
                    />
                  </div>
                </div>

                {templateForm.format === 'in_person' && (
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'المكان' : 'Place'}
                    </label>
                    <input
                      type="text"
                      value={templateForm.place}
                      onChange={e => setTemplateForm(prev => ({ ...prev, place: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14
                      }}
                    />
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'الحد الأقصى للطلاب' : 'Max Students'}
                    </label>
                    <input
                      type="number"
                      value={templateForm.max_students}
                      onChange={e => setTemplateForm(prev => ({ ...prev, max_students: parseInt(e.target.value) || 10 }))}
                      min="1"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                      {isArabic ? 'السعر (د.ك)' : 'Price (KD)'}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={templateForm.price}
                      onChange={e => setTemplateForm(prev => ({ ...prev, price: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        border: '2px solid #e5e7eb',
                        borderRadius: 8,
                        fontSize: 14
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    {isArabic ? 'الوصف' : 'Description'}
                  </label>
                  <textarea
                    value={templateForm.description}
                    onChange={e => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '2px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: 14,
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
                  <button
                    onClick={handleSaveTemplate}
                    disabled={submitting}
                    style={{
                      padding: '14px 24px',
                      background: submitting ? '#9ca3af' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 10,
                      fontWeight: 700,
                      fontSize: 15,
                      cursor: submitting ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {submitting 
                      ? (isArabic ? 'جاري الحفظ...' : 'Saving...')
                      : (isArabic ? 'حفظ' : 'Save')
                    }
                  </button>
                  <button
                    onClick={() => {
                      setTemplateModalOpen(false);
                      setEditingTemplate(null);
                      setTemplateForm({
                        name: '',
                        title: '',
                        teacher_name: '',
                        teacher_email: '',
                        teacher_image: '',
                        subject: '',
                        session_type: 'session',
                        format: 'in_person',
                        place: '',
                        time_from: '',
                        time_to: '',
                        max_students: 10,
                        description: '',
                        price: ''
                      });
                    }}
                    disabled={submitting}
                    style={{
                      padding: '14px 24px',
                      background: '#f3f4f6',
                      color: '#374151',
                      border: 'none',
                      borderRadius: 10,
                      fontWeight: 700,
                      fontSize: 15,
                      cursor: submitting ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isArabic ? 'إلغاء' : 'Cancel'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

